/**
 * Copyright (c) 2019 2020 2021 2022 AIGIA. All Rights Reserved.
 * see license.txt
 */

"use strict";

interface IPosition {
  frame: number;
  metrics: number;
  unit: string;
  criterion: number;
  level: string;
}

interface IPositionWithError extends IPosition {
  error: boolean;
}

interface IPhase {
  address: IPositionWithError;
  backswing: IPositionWithError;
  top: IPositionWithError;
  halfdown: IPositionWithError;
  impact: IPositionWithError;
  follow: IPositionWithError;
  finish: IPosition;
}

interface ISkillMonitor {
  前傾角度: IPhase;
  背骨の傾き: IPhase;
  首元の動き: IPhase;
  右腰の動き: IPhase;
  左腰の動き: IPhase;
  重心の動き_正面: IPhase;
  右膝の角度: IPhase;
  手元の浮き: IPhase;
  左膝の角度: IPhase;
  頭の動き: IPhase;
  両肩の傾き_正面: IPhase;
  重心の動き_後方: IPhase;
  グリップ位置: IPhase;
  手首軌道: IPhase;
  腰の開き: IPhase;
  肩の開き: IPhase;
}

interface positionData {
  AD: number;
  HB: number;
  TP: number;
  HD: number;
  IM: number;
  HF: number;
}

/*
 * スコア計算
 */
export class ShotScore {
  public statistics: any;
  public subtype: string = "スコア";
  public subtypes: string[] = ["スコア"];

  public clubs: { name: string; value: number }[] = [
    { name: "全て", value: 0 },
    { name: "1W", value: 1 },
    { name: "3W", value: 2 },
    { name: "5W", value: 3 },
    { name: "7W", value: 4 },
    { name: "UT", value: 5 },
    { name: "3I", value: 6 },
    { name: "4I", value: 7 },
    { name: "5I", value: 8 },
    { name: "6I", value: 9 },
    { name: "7I", value: 10 },
    { name: "8I", value: 11 },
    { name: "9I", value: 12 },
    { name: "PW", value: 13 },
    { name: "AW", value: 14 },
  ];

  /*
   * valueはHighと同じか、より小さくLowと同じか、より大きいか？
   */
  private static between(low: number, value: number, high: number): boolean {
    return low < value && value < high;
  }

  /*
   * valueはHighより小さくLowより大きいか？
   */
  private static betweenEqual(
    low: number,
    value: number,
    high: number
  ): boolean {
    return low <= value && value <= high;
  }

  /*
   * varを100%とした時、par%の値
   */
  private static percent(value: number, par: number): number {
    return (value / 100) * par;
  }

  /*
   * 度/Rad
   */
  // protected degToRad(deg: number): number {
  // 	return deg * (Math.PI / 180);
  // }

  /*
   * 斜辺と角度から底辺
   */
  // protected sideToBase(deg: number, side: number): number {
  // 	return Math.sin(this.degToRad(deg)) * side;
  // }

  /*
   *
   */
  private find(
    list: { Name: string; Value: number }[],
    name: string
  ): { Name: string; Value: number } {
    let result: { Name: string; Value: number } = null;
    list.forEach((element: { Name: string; Value: number }) => {
      if (element.Name === name) {
        result = element;
      }
    });
    return result;
  }

  /*
   *
   */
  private valueByName(
    list: { Name: string; Value: number }[],
    name: string
  ): number {
    let result: number = 0;
    const element: { Name: string; Value: number } = this.find(list, name);
    if (element) {
      result = element.Value;
    }
    return result;
  }

  /*
   *
   */
  private calcBallisticScore(list: any[], club: number): number {
    let result: number = 0;
    const トータル: number = this.valueByName(list, "トータル");
    const 左右ブレ: number = this.valueByName(list, "左右ブレ");
    const キャリー: number = this.valueByName(list, "キャリー");
    const キャリーブレ: number = this.valueByName(list, "キャリーブレ");
    const ヘッドスピード: number = this.valueByName(list, "ヘッドスピード");
    const ボールスピード: number = this.valueByName(list, "ボールスピード");
    const ミート率: number = this.valueByName(list, "ミート率");
    const 打ち出し角_上下: number = this.valueByName(list, "打ち出し角 上下");
    const 打ち出し角_左右: number = this.valueByName(list, "打ち出し角 左右");
    const バックスピン: number = this.valueByName(list, "バックスピン");
    const サイドスピン: number = this.valueByName(list, "サイドスピン");
    const ブロー角: number = this.valueByName(list, "ブロー角");
    const ヘッド軌道: number = this.valueByName(list, "ヘッド軌道");
    const フェイス角: number = this.valueByName(list, "フェイス角");

    switch (club) {
      case 1: // 1W
        if (ShotScore.betweenEqual(13, 打ち出し角_上下, 18)) {
          result += 5;
        }

        if (ShotScore.betweenEqual(-2, 打ち出し角_左右, 2)) {
          result += 15;
        } else if (ShotScore.betweenEqual(-4, 打ち出し角_左右, 4)) {
          result += 10;
        } else if (ShotScore.betweenEqual(-7, 打ち出し角_左右, 7)) {
          result += 5;
        }

        if (ShotScore.betweenEqual(2300, バックスピン, 2700)) {
          result += 8;
        }

        if (ShotScore.betweenEqual(-200, サイドスピン, 200)) {
          result += 15;
        } else if (ShotScore.betweenEqual(-350, サイドスピン, 350)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-600, サイドスピン, 600)) {
          result += 2;
        }

        const 最大飛距離 = ボールスピード * 4;
        if (ShotScore.percent(最大飛距離, 90) <= トータル) {
          result += 20;
        } else if (ShotScore.percent(最大飛距離, 80) <= トータル) {
          result += 10;
        } else if (ShotScore.percent(最大飛距離, 70) <= トータル) {
          result += 5;
        }

        if (ShotScore.betweenEqual(-10, 左右ブレ, 10)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-25, 左右ブレ, 25)) {
          result += 10;
        } else if (ShotScore.betweenEqual(-30, 左右ブレ, 30)) {
          result += 5;
        }

        if (1.43 <= ミート率) {
          result += 20;
        } else if (1.4 <= ミート率) {
          result += 12;
        } else if (1.37 <= ミート率) {
          result += 6;
        }
        break;
      case 3: // 5W
        if (ShotScore.betweenEqual(10, 打ち出し角_上下, 25)) {
          result += 4;
        }

        if (ShotScore.betweenEqual(-2, 打ち出し角_左右, 2)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-4, 打ち出し角_左右, 4)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-7, 打ち出し角_左右, 7)) {
          result += 2;
        }

        if (ShotScore.betweenEqual(3000, バックスピン, 6000)) {
          result += 5;
        }

        if (ShotScore.betweenEqual(-300, サイドスピン, 300)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-400, サイドスピン, 400)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-600, サイドスピン, 600)) {
          result += 2;
        }

        const ターゲット飛距離_5W = ボールスピード * 3.5;
        if (ShotScore.percent(ターゲット飛距離_5W, 90) <= トータル) {
          result += 15;
        } else if (ShotScore.percent(ターゲット飛距離_5W, 85) <= トータル) {
          result += 8;
        } else if (ShotScore.percent(ターゲット飛距離_5W, 80) <= トータル) {
          result += 6;
        }

        if (ShotScore.betweenEqual(-10, 左右ブレ, 10)) {
          result += 27;
        } else if (ShotScore.betweenEqual(-20, 左右ブレ, 20)) {
          result += 12;
        } else if (ShotScore.betweenEqual(-30, 左右ブレ, 30)) {
          result += 5;
        }

        if (1.4 <= ミート率) {
          result += 15;
        } else if (1.35 <= ミート率) {
          result += 8;
        } else if (1.3 <= ミート率) {
          result += 2;
        }
        break;
      case 10: // 7I
        if (ShotScore.betweenEqual(17, 打ち出し角_上下, 27)) {
          result += 4;
        }

        if (ShotScore.betweenEqual(-2, 打ち出し角_左右, 2)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-4, 打ち出し角_左右, 4)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-7, 打ち出し角_左右, 7)) {
          result += 2;
        }

        if (ShotScore.betweenEqual(3000, バックスピン, 7000)) {
          result += 5;
        }

        if (ShotScore.betweenEqual(-300, サイドスピン, 300)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-400, サイドスピン, 400)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-600, サイドスピン, 600)) {
          result += 2;
        }

        const ターゲット飛距離_7I = ボールスピード * 3.3;
        if (
          ShotScore.betweenEqual(
            ShotScore.percent(ターゲット飛距離_7I, 90),
            トータル,
            ShotScore.percent(ターゲット飛距離_7I, 110)
          )
        ) {
          result += 15;
        } else if (
          ShotScore.betweenEqual(
            ShotScore.percent(ターゲット飛距離_7I, 85),
            トータル,
            ShotScore.percent(ターゲット飛距離_7I, 115)
          )
        ) {
          result += 8;
        } else if (
          ShotScore.betweenEqual(
            ShotScore.percent(ターゲット飛距離_7I, 80),
            トータル,
            ShotScore.percent(ターゲット飛距離_7I, 120)
          )
        ) {
          result += 6;
        }

        if (ShotScore.betweenEqual(-5, 左右ブレ, 5)) {
          result += 27;
        } else if (ShotScore.betweenEqual(-20, 左右ブレ, 20)) {
          result += 12;
        } else if (ShotScore.betweenEqual(-30, 左右ブレ, 30)) {
          result += 5;
        }

        if (1.26 <= ミート率) {
          result += 15;
        } else if (1.2 <= ミート率) {
          result += 8;
        } else {
          result += 2;
        }
        break;
      case 13: // PW
        if (ShotScore.betweenEqual(21, 打ち出し角_上下, 36)) {
          result += 4;
        }

        if (ShotScore.betweenEqual(-2, 打ち出し角_左右, 2)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-4, 打ち出し角_左右, 4)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-7, 打ち出し角_左右, 7)) {
          result += 2;
        }

        if (ShotScore.betweenEqual(6000, バックスピン, 11000)) {
          result += 5;
        }

        // todo: score
        if (ShotScore.betweenEqual(-500, サイドスピン, 500)) {
          result += 17;
        } else if (ShotScore.betweenEqual(-600, サイドスピン, 600)) {
          result += 8;
        } else if (ShotScore.betweenEqual(-700, サイドスピン, 700)) {
          result += 2;
        }

        const ターゲット飛距離_PW = ボールスピード * 2.7;
        if (
          ShotScore.betweenEqual(
            ShotScore.percent(ターゲット飛距離_PW, 90),
            トータル,
            ShotScore.percent(ターゲット飛距離_PW, 110)
          )
        ) {
          result += 15;
        } else if (
          ShotScore.betweenEqual(
            ShotScore.percent(ターゲット飛距離_PW, 85),
            トータル,
            ShotScore.percent(ターゲット飛距離_PW, 115)
          )
        ) {
          result += 8;
        } else if (
          ShotScore.betweenEqual(
            ShotScore.percent(ターゲット飛距離_PW, 80),
            トータル,
            ShotScore.percent(ターゲット飛距離_PW, 120)
          )
        ) {
          result += 6;
        }

        if (ShotScore.betweenEqual(-10, 左右ブレ, 10)) {
          result += 27;
        } else if (ShotScore.betweenEqual(-20, 左右ブレ, 20)) {
          result += 12;
        } else if (ShotScore.betweenEqual(-30, 左右ブレ, 30)) {
          result += 5;
        }

        if (1.18 <= ミート率) {
          result += 15;
        } else if (1.13 <= ミート率) {
          result += 8;
        } else if (1.08 <= ミート率) {
          result += 2;
        }
        break;
      default:
        break;
    }
    return result;
  }

  /*
   *
   */
  private phase(
    data: any,
    phase_name: string,
    callback: (
      AD: number,
      HB: number,
      TP: number,
      HD: number,
      IM: number,
      HF: number,
      FN: number
    ) => void
  ) {
    const _AD: (data: any) => number = (data: any): number => {
      return Math.round(data.address.metrics);
    };

    const _HB: (data: any) => number = (data: any): number => {
      return Math.round(data.backswing.metrics);
    };

    const _TP: (data: any) => number = (data: any): number => {
      return Math.round(data.top.metrics);
    };

    const _HD: (data: any) => number = (data: any): number => {
      return Math.round(data.halfdown.metrics);
    };

    const _IM: (data: any) => number = (data: any): number => {
      return Math.round(data.impact.metrics);
    };

    const _HF: (data: any) => number = (data: any): number => {
      return Math.round(data.follow.metrics);
    };

    const _FN: (data: any) => number = (data: any): number => {
      return Math.round(data.finish.metrics);
    };

    const dataParPhase: (data: any, phase: string) => any = (
      data: any,
      phase: string
    ): any => {
      return data[phase];
    };

    const phase: any = dataParPhase(data, phase_name);
    const AD: number = _AD(phase);
    const HB: number = _HB(phase);
    const TP: number = _TP(phase);
    const HD: number = _HD(phase);
    const IM: number = _IM(phase);
    const HF: number = _HF(phase);
    const FN: number = _FN(phase);
    callback(AD, HB, TP, HD, IM, HF, FN);
  }

  public markDatas = [
    {
      part: "前傾角度", // part 1
      rules: ["22-A", "12-B", "5-C", "1-D", "0-other"],
    },
    {
      part: "重心の動き_正面", // part 2
      rules: ["20-A", "10-B", "1-C", "0-other"],
    },
    {
      part: "背骨の傾き", // part 3
      rules: ["15-A", "6-B", "1-C", "0-other"],
    },
    {
      part: "右腰の動き", // part 4
      rules: ["10-A", "4-B", "1-C", "0-other"],
    },
    {
      part: "左腰の動き", // part 5
      rules: ["10-A", "4-B", "1-C", "0-other"],
    },
    {
      part: "首元の動き", // part 6
      rules: ["8-A", "4-B", "1-C", "0-other"],
    },
    {
      part: "右膝の角度", // part 7
      rules: ["4-A", "1-B", "0-other"],
    },
    {
      part: "左膝の角度", // part 8
      rules: ["4-A", "1-B", "0-other"],
    },
  ];

  public setMark(result2, part, markNumber) {
    let partData = result2[part];
    let mark = partData.rules.find((x) => x.mark_number == markNumber);
    partData.score = mark;
    result2.total_score += mark.mark_number;
  }
  // 1W
  public postureScoreOf1W(data: any, isGetMark: boolean = false): number {
    let result: number = 7; // 無条件で7点は入るので初期値を7に設定
    let result2: any = {
      total_score: result,
    };

    if (isGetMark) {
      this.markDatas.forEach((d) => {
        result2[d.part] = {
          score: null,
          rules: d.rules.map((x) => ({
            mark_number: Number(x.split("-")[0]),
            mark_alpha: x.split("-")[1],
          })),
        };
        this.setMark(result2, d.part, 0);
      });
    }

    // part 1
    this.phase(
      data,
      "前傾角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 8, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 8, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 10, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 10, IM, AD + 3) &&
          ShotScore.betweenEqual(AD - 18, HF, AD + 3)
        ) {
          result += 22;
          if (isGetMark) this.setMark(result2, "前傾角度", 22);
        } else if (
          ShotScore.betweenEqual(AD - 12, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 12, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 14, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 14, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 21, HF, AD + 3)
        ) {
          result += 12;
          if (isGetMark) this.setMark(result2, "前傾角度", 12);
        } else if (
          ShotScore.betweenEqual(AD - 16, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 16, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 18, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 18, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 24, HF, AD + 5)
        ) {
          result += 5;
          if (isGetMark) this.setMark(result2, "前傾角度", 5);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "前傾角度", 1);
        }
      }
    );

    // part 2
    this.phase(
      data,
      "重心の動き_正面",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-7, AD, 0) &&
          ShotScore.betweenEqual(-7, HB, 0) &&
          ShotScore.betweenEqual(-9, TP, 0) &&
          ShotScore.betweenEqual(-5, HD, 4) &&
          ShotScore.betweenEqual(-4, IM, 5) &&
          ShotScore.betweenEqual(-3, HF, 5)
        ) {
          result += 20;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 20);
        } else if (
          ShotScore.betweenEqual(-10, AD, 3) &&
          ShotScore.betweenEqual(-10, HB, 3) &&
          ShotScore.betweenEqual(-12, TP, 3) &&
          ShotScore.betweenEqual(-7, HD, 6) &&
          ShotScore.betweenEqual(-6, IM, 8) &&
          ShotScore.betweenEqual(-3, HF, 15)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 10);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 1);
        }
      }
    );

    // part 3
    this.phase(
      data,
      "背骨の傾き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-6, AD, 3) &&
          ShotScore.betweenEqual(-6, HB, 3) &&
          ShotScore.betweenEqual(-8, TP, 3) &&
          ShotScore.betweenEqual(-12, HD, 0) &&
          ShotScore.betweenEqual(-16, IM, -3) &&
          ShotScore.betweenEqual(-16, HF, -1)
        ) {
          result += 15;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 15);
        } else if (
          ShotScore.betweenEqual(-10, AD, 7) &&
          ShotScore.betweenEqual(-10, HB, 7) &&
          ShotScore.betweenEqual(-12, TP, 7) &&
          ShotScore.betweenEqual(-16, HD, 3) &&
          ShotScore.betweenEqual(-19, IM, 4) &&
          ShotScore.betweenEqual(-20, HF, 5)
        ) {
          result += 6;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 6);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 1);
        }
      }
    );

    // part 4
    this.phase(
      data,
      "右腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 5, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 5, TP, AD + 3) &&
          ShotScore.betweenEqual(AD - 4, HD, AD + 8) &&
          ShotScore.betweenEqual(AD + 2, IM, AD + 10) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "右腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 8, HB, AD + 4) &&
          ShotScore.betweenEqual(AD - 8, TP, AD + 6) &&
          ShotScore.betweenEqual(AD - 7, HD, AD + 14) &&
          ShotScore.betweenEqual(AD, IM, AD + 18) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 25)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右腰の動き", 1);
        }
      }
    );

    // part 5
    this.phase(
      data,
      "左腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD, HB, AD + 12) &&
          ShotScore.betweenEqual(AD, TP, AD + 12) &&
          ShotScore.betweenEqual(AD - 4, HD, AD + 5) &&
          ShotScore.betweenEqual(AD - 8, IM, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HF, AD + 5)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "左腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 4, HB, AD + 16) &&
          ShotScore.betweenEqual(AD - 4, TP, AD + 16) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 9) &&
          ShotScore.betweenEqual(AD - 12, IM, AD + 6) &&
          ShotScore.betweenEqual(AD - 14, HF, AD + 14)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左腰の動き", 1);
        }
      }
    );

    // part 6
    this.phase(
      data,
      "首元の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-10, HB, 0) &&
          ShotScore.betweenEqual(-11, TP, -1) &&
          ShotScore.betweenEqual(-10, HD, 1) &&
          ShotScore.betweenEqual(-12, IM, 2) &&
          ShotScore.betweenEqual(-9, HF, 2)
        ) {
          result += 8;
          if (isGetMark) this.setMark(result2, "首元の動き", 8);
        } else if (
          ShotScore.betweenEqual(-14, HB, 4) &&
          ShotScore.betweenEqual(-15, TP, 3) &&
          ShotScore.betweenEqual(-12, HD, 3) &&
          ShotScore.betweenEqual(-16, IM, 4) &&
          ShotScore.betweenEqual(-13, HF, 4)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "首元の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "首元の動き", 1);
        }
      }
    );

    // part 7
    this.phase(
      data,
      "右膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(16, AD, 38) &&
          ShotScore.betweenEqual(8, HB, 40) &&
          ShotScore.betweenEqual(2, TP, 40) &&
          ShotScore.betweenEqual(25, HD, 65) &&
          ShotScore.betweenEqual(25, IM, 55) &&
          ShotScore.betweenEqual(20, HF, 50) &&
          ShotScore.betweenEqual(-1, FN, 40)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右膝の角度", 1);
        }
      }
    );

    // part 8
    this.phase(
      data,
      "左膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(6, AD, 33) &&
          ShotScore.betweenEqual(8, HB, 38) &&
          ShotScore.betweenEqual(14, TP, 55) &&
          ShotScore.betweenEqual(25, HD, 58) &&
          ShotScore.betweenEqual(16, IM, 45) &&
          ShotScore.betweenEqual(7, HF, 35)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左膝の角度", 1);
        }
      }
    );

    return !isGetMark ? result : result2;
  }

  /*
   *
   */
  public postureScoreOf5W(data: any, isGetMark: boolean = false): number {
    let result: number = 7;
    let result2: any = {
      total_score: result,
    };

    if (isGetMark) {
      this.markDatas.forEach((d) => {
        result2[d.part] = {
          score: null,
          rules: d.rules.map((x) => ({
            mark_number: Number(x.split("-")[0]),
            mark_alpha: x.split("-")[1],
          })),
        };
        this.setMark(result2, d.part, 0);
      });
    }

    // part 1
    this.phase(
      data,
      "前傾角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 7, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 7, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 8, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 12, HF, AD + 4)
        ) {
          result += 22;
          if (isGetMark) this.setMark(result2, "前傾角度", 22);
        } else if (
          ShotScore.betweenEqual(AD - 11, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 11, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 12, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 13, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 16, HF, AD + 4)
        ) {
          result += 12;
          if (isGetMark) this.setMark(result2, "前傾角度", 12);
        } else if (
          ShotScore.betweenEqual(AD - 15, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 15, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 16, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 17, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 20, HF, AD + 4)
        ) {
          result += 5;
          if (isGetMark) this.setMark(result2, "前傾角度", 5);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "前傾角度", 1);
        }
      }
    );

    // part 2
    this.phase(
      data,
      "重心の動き_正面",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-7, AD, 1) &&
          ShotScore.betweenEqual(-7, HB, 1) &&
          ShotScore.betweenEqual(-8, TP, 1) &&
          ShotScore.betweenEqual(-5, HD, 3) &&
          ShotScore.betweenEqual(-3, IM, 5) &&
          ShotScore.betweenEqual(-2, HF, 8)
        ) {
          result += 20;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 20);
        } else if (
          ShotScore.betweenEqual(-10, AD, 4) &&
          ShotScore.betweenEqual(-10, HB, 4) &&
          ShotScore.betweenEqual(-11, TP, 4) &&
          ShotScore.betweenEqual(-7, HD, 5) &&
          ShotScore.betweenEqual(-5, IM, 8) &&
          ShotScore.betweenEqual(-5, HF, 11)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 10);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 1);
        }
      }
    );

    // part 3
    this.phase(
      data,
      "背骨の傾き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-7, AD, 3) &&
          ShotScore.betweenEqual(-8, HB, 2) &&
          ShotScore.betweenEqual(-8, TP, 3) &&
          ShotScore.betweenEqual(-8, HD, 2) &&
          ShotScore.betweenEqual(-12, IM, -1) &&
          ShotScore.betweenEqual(-14, HF, 1)
        ) {
          result += 15;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 15);
        } else if (
          ShotScore.betweenEqual(-11, AD, 7) &&
          ShotScore.betweenEqual(-12, HB, 6) &&
          ShotScore.betweenEqual(-12, TP, 7) &&
          ShotScore.betweenEqual(-12, HD, 6) &&
          ShotScore.betweenEqual(-14, IM, 4) &&
          ShotScore.betweenEqual(-18, HF, 5)
        ) {
          result += 6;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 6);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 1);
        }
      }
    );

    // part 4
    this.phase(
      data,
      "右腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 5, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 5, TP, AD + 3) &&
          ShotScore.betweenEqual(AD - 5, HD, AD + 8) &&
          ShotScore.betweenEqual(AD + 2, IM, AD + 9) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "右腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 10, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 10, TP, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 12) &&
          ShotScore.betweenEqual(AD, IM, AD + 14) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右腰の動き", 1);
        }
      }
    );

    // part 5
    this.phase(
      data,
      "左腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD, HB, AD + 12) &&
          ShotScore.betweenEqual(AD, TP, AD + 12) &&
          ShotScore.betweenEqual(AD - 4, HD, AD + 3) &&
          ShotScore.betweenEqual(AD - 8, IM, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HF, AD + 5)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "左腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 4, HB, AD + 16) &&
          ShotScore.betweenEqual(AD - 4, TP, AD + 16) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 12, IM, AD + 6) &&
          ShotScore.betweenEqual(AD - 14, HF, AD + 14)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左腰の動き", 1);
        }
      }
    );

    // part 6
    this.phase(
      data,
      "首元の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-10, HB, 0) &&
          ShotScore.betweenEqual(-9, TP, 0) &&
          ShotScore.betweenEqual(-6, HD, 3) &&
          ShotScore.betweenEqual(-4, IM, 3) &&
          ShotScore.betweenEqual(-3, HF, 4)
        ) {
          result += 8;
          if (isGetMark) this.setMark(result2, "首元の動き", 8);
        } else if (
          ShotScore.betweenEqual(-14, HB, 4) &&
          ShotScore.betweenEqual(-13, TP, 4) &&
          ShotScore.betweenEqual(-8, HD, 5) &&
          ShotScore.betweenEqual(-8, IM, 5) &&
          ShotScore.betweenEqual(-7, HF, 6)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "首元の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "首元の動き", 1);
        }
      }
    );

    // part 7
    this.phase(
      data,
      "右膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(15, AD, 40) &&
          ShotScore.betweenEqual(8, HB, 40) &&
          ShotScore.betweenEqual(2, TP, 40) &&
          ShotScore.betweenEqual(25, HD, 65) &&
          ShotScore.betweenEqual(25, IM, 55) &&
          ShotScore.betweenEqual(20, HF, 50)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右膝の角度", 1);
        }
      }
    );

    // part 8
    this.phase(
      data,
      "左膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(10, AD, 40) &&
          ShotScore.betweenEqual(8, HB, 42) &&
          ShotScore.betweenEqual(14, TP, 52) &&
          ShotScore.betweenEqual(25, HD, 52) &&
          ShotScore.betweenEqual(15, IM, 45) &&
          ShotScore.betweenEqual(12, HF, 45)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左膝の角度", 1);
        }
      }
    );
    return !isGetMark ? result : result2;
  }

  /*
   *
   */
  public postureScoreOf7I(data: any, isGetMark: boolean = false): number {
    let result: number = 7; // 無条件で7点は入るので初期値を7に設定
    let result2: any = {
      total_score: result,
    };

    if (isGetMark) {
      this.markDatas.forEach((d) => {
        result2[d.part] = {
          score: null,
          rules: d.rules.map((x) => ({
            mark_number: Number(x.split("-")[0]),
            mark_alpha: x.split("-")[1],
          })),
        };
        this.setMark(result2, d.part, 0);
      });
    }

    // part 1
    this.phase(
      data,
      "前傾角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 6, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 6, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 6, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 7, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 13, HF, AD + 4)
        ) {
          result += 22;
          if (isGetMark) this.setMark(result2, "前傾角度", 22);
        } else if (
          ShotScore.betweenEqual(AD - 10, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 10, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 10, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 12, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 17, HF, AD + 4)
        ) {
          result += 12;
          if (isGetMark) this.setMark(result2, "前傾角度", 12);
        } else if (
          ShotScore.betweenEqual(AD - 14, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 14, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 14, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 16, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 23, HF, AD + 4)
        ) {
          result += 5;
          if (isGetMark) this.setMark(result2, "前傾角度", 5);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "前傾角度", 1);
        }
      }
    );

    // part 2
    this.phase(
      data,
      "重心の動き_正面",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-7, AD, 1) &&
          ShotScore.betweenEqual(-7, HB, 1) &&
          ShotScore.betweenEqual(-8, TP, 1) &&
          ShotScore.betweenEqual(-4, HD, 5) &&
          ShotScore.betweenEqual(-1, IM, 6) &&
          ShotScore.betweenEqual(0, HF, 13)
        ) {
          result += 20;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 20);
        } else if (
          ShotScore.betweenEqual(-10, AD, 4) &&
          ShotScore.betweenEqual(-10, HB, 4) &&
          ShotScore.betweenEqual(-11, TP, 4) &&
          ShotScore.betweenEqual(-6, HD, 7) &&
          ShotScore.betweenEqual(-3, IM, 10) &&
          ShotScore.betweenEqual(-3, HF, 13)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 10);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 1);
        }
      }
    );

    // part 3
    this.phase(
      data,
      "背骨の傾き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-7, AD, 3) &&
          ShotScore.betweenEqual(-8, HB, 2) &&
          ShotScore.betweenEqual(-8, TP, 3) &&
          ShotScore.betweenEqual(-8, HD, 2) &&
          ShotScore.betweenEqual(-12, IM, -1) &&
          ShotScore.betweenEqual(-14, HF, 1)
        ) {
          result += 15;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 15);
        } else if (
          ShotScore.betweenEqual(-11, AD, 7) &&
          ShotScore.betweenEqual(-12, HB, 6) &&
          ShotScore.betweenEqual(-12, TP, 7) &&
          ShotScore.betweenEqual(-12, HD, 6) &&
          ShotScore.betweenEqual(-14, IM, 4) &&
          ShotScore.betweenEqual(-18, HF, 5)
        ) {
          result += 6;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 6);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 1);
        }
      }
    );

    // part 4
    this.phase(
      data,
      "右腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 5, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 5, TP, AD + 3) &&
          ShotScore.betweenEqual(AD - 5, HD, AD + 8) &&
          ShotScore.betweenEqual(AD + 2, IM, AD + 9) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "右腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 10, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 10, TP, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 12) &&
          ShotScore.betweenEqual(AD, IM, AD + 14) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右腰の動き", 1);
        }
      }
    );

    // part 5
    this.phase(
      data,
      "左腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD, HB, AD + 12) &&
          ShotScore.betweenEqual(AD, TP, AD + 12) &&
          ShotScore.betweenEqual(AD - 4, HD, AD + 3) &&
          ShotScore.betweenEqual(AD - 8, IM, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HF, AD + 5)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "左腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 4, HB, AD + 16) &&
          ShotScore.betweenEqual(AD - 4, TP, AD + 16) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 12, IM, AD + 6) &&
          ShotScore.betweenEqual(AD - 14, HF, AD + 14)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左腰の動き", 1);
        }
      }
    );

    // part 6
    this.phase(
      data,
      "首元の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-10, HB, 0) &&
          ShotScore.betweenEqual(-9, TP, 0) &&
          ShotScore.betweenEqual(-6, HD, 3) &&
          ShotScore.betweenEqual(-4, IM, 3) &&
          ShotScore.betweenEqual(-3, HF, 4)
        ) {
          result += 8;
          if (isGetMark) this.setMark(result2, "首元の動き", 8);
        } else if (
          ShotScore.betweenEqual(-14, HB, 4) &&
          ShotScore.betweenEqual(-13, TP, 4) &&
          ShotScore.betweenEqual(-8, HD, 5) &&
          ShotScore.betweenEqual(-8, IM, 5) &&
          ShotScore.betweenEqual(-7, HF, 6)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "首元の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "首元の動き", 1);
        }
      }
    );

    // part 7
    this.phase(
      data,
      "右膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(15, AD, 40) &&
          ShotScore.betweenEqual(8, HB, 40) &&
          ShotScore.betweenEqual(2, TP, 40) &&
          ShotScore.betweenEqual(25, HD, 65) &&
          ShotScore.betweenEqual(25, IM, 55) &&
          ShotScore.betweenEqual(20, HF, 50)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右膝の角度", 1);
        }
      }
    );

    // part 8
    this.phase(
      data,
      "左膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(10, AD, 40) &&
          ShotScore.betweenEqual(8, HB, 42) &&
          ShotScore.betweenEqual(14, TP, 52) &&
          ShotScore.betweenEqual(25, HD, 52) &&
          ShotScore.betweenEqual(15, IM, 45) &&
          ShotScore.betweenEqual(12, HF, 45)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左膝の角度", 1);
        }
      }
    );

    return !isGetMark ? result : result2;
  }

  /*
   *
   */
  public postureScoreOfPW(data: any, isGetMark: boolean = false): number {
    let result: number = 7;
    let result2: any = {
      total_score: result,
    };

    // marks data
    if (isGetMark) {
      this.markDatas.forEach((d) => {
        result2[d.part] = {
          score: null,
          rules: d.rules.map((x) => ({
            mark_number: Number(x.split("-")[0]),
            mark_alpha: x.split("-")[1],
          })),
        };
        this.setMark(result2, d.part, 0);
      });
    }
    // todo: score
    /*
		// part 1
		this.phase(data, "前傾角度", (AD: number, HB: number, TP: number, HD: number, IM: number, HF: number, FN: number) => {

			if (
				ShotScore.betweenEqual(AD - 4, HB, AD + 3) &&
				ShotScore.betweenEqual(AD - 4, TP, AD + 3) &&
				ShotScore.betweenEqual(AD - 4, HD, AD + 4) &&
				ShotScore.betweenEqual(AD - 4, IM, AD + 4) &&
				ShotScore.betweenEqual(AD - 13, HF, AD + 4)
			) {
				result += 22;
			} else if (
				ShotScore.betweenEqual(AD - 6, HB, AD + 5) &&
				ShotScore.betweenEqual(AD - 6, TP, AD + 5) &&
				ShotScore.betweenEqual(AD - 6, HD, AD + 5) &&
				ShotScore.betweenEqual(AD - 6, IM, AD + 5) &&
				ShotScore.betweenEqual(AD - 17, HF, AD + 4)
			) {
				result += 12;
			} else if (
				ShotScore.betweenEqual(AD - 8, HB, AD + 7) &&
				ShotScore.betweenEqual(AD - 8, TP, AD + 7) &&
				ShotScore.betweenEqual(AD - 8, HD, AD + 6) &&
				ShotScore.betweenEqual(AD - 8, IM, AD + 6) &&
				ShotScore.betweenEqual(AD - 23, HF, AD + 4)
			) {
				result += 5;
			} else {
				result += 1;
			}
		})
*/

    // part 1
    this.phase(
      data,
      "前傾角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 6, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 6, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 6, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 7, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 13, HF, AD + 4)
        ) {
          result += 22;
          if (isGetMark) this.setMark(result2, "前傾角度", 22);
        } else if (
          ShotScore.betweenEqual(AD - 10, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 10, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 10, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 12, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 17, HF, AD + 4)
        ) {
          result += 12;
          if (isGetMark) this.setMark(result2, "前傾角度", 12);
        } else if (
          ShotScore.betweenEqual(AD - 14, HB, AD + 5) &&
          ShotScore.betweenEqual(AD - 14, TP, AD + 5) &&
          ShotScore.betweenEqual(AD - 14, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 16, IM, AD + 5) &&
          ShotScore.betweenEqual(AD - 23, HF, AD + 4)
        ) {
          result += 5;
          if (isGetMark) this.setMark(result2, "前傾角度", 5);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "前傾角度", 1);
        }
      }
    );

    // part 2
    this.phase(
      data,
      "重心の動き_正面",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-5, AD, 2) &&
          ShotScore.betweenEqual(-5, HB, 2) &&
          ShotScore.betweenEqual(-6, TP, 2) &&
          ShotScore.betweenEqual(-4, HD, 4) &&
          ShotScore.betweenEqual(-1, IM, 7) &&
          ShotScore.betweenEqual(0, HF, 10)
        ) {
          result += 20;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 20);
        } else if (
          ShotScore.betweenEqual(-7, AD, 4) &&
          ShotScore.betweenEqual(-7, HB, 4) &&
          ShotScore.betweenEqual(-8, TP, 4) &&
          ShotScore.betweenEqual(-6, HD, 6) &&
          ShotScore.betweenEqual(-3, IM, 10) &&
          ShotScore.betweenEqual(-3, HF, 13)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 10);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "重心の動き_正面", 1);
        }
      }
    );

    // part 3
    this.phase(
      data,
      "背骨の傾き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-6, AD, 3) &&
          ShotScore.betweenEqual(-6, HB, 3) &&
          ShotScore.betweenEqual(-6, TP, 3) &&
          ShotScore.betweenEqual(-6, HD, 3) &&
          ShotScore.betweenEqual(-8, IM, 3) &&
          ShotScore.betweenEqual(-10, HF, 3)
        ) {
          result += 15;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 15);
        } else if (
          ShotScore.betweenEqual(-8, AD, 5) &&
          ShotScore.betweenEqual(-8, HB, 5) &&
          ShotScore.betweenEqual(-8, TP, 5) &&
          ShotScore.betweenEqual(-8, HD, 5) &&
          ShotScore.betweenEqual(-8, IM, 5) &&
          ShotScore.betweenEqual(-12, HF, 5)
        ) {
          result += 6;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 6);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "背骨の傾き", 1);
        }
      }
    );
    // todo: score
    /*
		// part 4
		this.phase(data, "右腰の動き", (AD: number, HB: number, TP: number, HD: number, IM: number, HF: number, FN: number) => {

			if (
				ShotScore.betweenEqual(AD - 4, HB, AD + 3) &&
				ShotScore.betweenEqual(AD - 4, TP, AD + 4) &&
				ShotScore.betweenEqual(AD - 5, HD, AD + 8) &&
				ShotScore.betweenEqual(AD + 2, IM, AD + 9) &&
				ShotScore.betweenEqual(AD + 2, HF, AD + 20)
			) {
				result += 10;
			} else if (
				ShotScore.betweenEqual(AD - 8, HB, AD + 3) &&
				ShotScore.betweenEqual(AD - 8, TP, AD + 4) &&
				ShotScore.betweenEqual(AD - 8, HD, AD + 12) &&
				ShotScore.betweenEqual(AD, IM, AD + 14) &&
				ShotScore.betweenEqual(AD + 2, HF, AD + 20)
			) {
				result += 4;
			} else {
				result += 1;
			}

		})

		// part 5
		this.phase(data, "左腰の動き", (AD: number, HB: number, TP: number, HD: number, IM: number, HF: number, FN: number) => {

			if (
				ShotScore.betweenEqual(AD, HB, AD + 12) &&
				ShotScore.betweenEqual(AD, TP, AD + 12) &&
				ShotScore.betweenEqual(AD - 5, HD, AD + 2) &&
				ShotScore.betweenEqual(AD - 6, IM, AD + 1) &&
				ShotScore.betweenEqual(AD - 6, HF, AD + 5)
			) {
				result += 10;
			} else if (
				ShotScore.betweenEqual(AD - 4, HB, AD + 16) &&
				ShotScore.betweenEqual(AD - 4, TP, AD + 16) &&
				ShotScore.betweenEqual(AD - 7, HD, AD + 4) &&
				ShotScore.betweenEqual(AD - 8, IM, AD + 3) &&
				ShotScore.betweenEqual(AD - 8, HF, AD + 7)
			) {
				result += 4;
			} else {
				result += 1;
			}

		})
*/

    // part 4
    this.phase(
      data,
      "右腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD - 5, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 5, TP, AD + 3) &&
          ShotScore.betweenEqual(AD - 5, HD, AD + 8) &&
          ShotScore.betweenEqual(AD + 2, IM, AD + 9) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "右腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 10, HB, AD + 2) &&
          ShotScore.betweenEqual(AD - 10, TP, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 12) &&
          ShotScore.betweenEqual(AD, IM, AD + 14) &&
          ShotScore.betweenEqual(AD + 2, HF, AD + 20)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右腰の動き", 1);
        }
      }
    );

    // part 5
    this.phase(
      data,
      "左腰の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(AD, HB, AD + 12) &&
          ShotScore.betweenEqual(AD, TP, AD + 12) &&
          ShotScore.betweenEqual(AD - 4, HD, AD + 3) &&
          ShotScore.betweenEqual(AD - 8, IM, AD + 2) &&
          ShotScore.betweenEqual(AD - 8, HF, AD + 5)
        ) {
          result += 10;
          if (isGetMark) this.setMark(result2, "左腰の動き", 10);
        } else if (
          ShotScore.betweenEqual(AD - 4, HB, AD + 16) &&
          ShotScore.betweenEqual(AD - 4, TP, AD + 16) &&
          ShotScore.betweenEqual(AD - 8, HD, AD + 7) &&
          ShotScore.betweenEqual(AD - 12, IM, AD + 6) &&
          ShotScore.betweenEqual(AD - 14, HF, AD + 14)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左腰の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左腰の動き", 1);
        }
      }
    );

    // part 6
    this.phase(
      data,
      "首元の動き",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(-10, HB, 0) &&
          ShotScore.betweenEqual(-9, TP, 0) &&
          ShotScore.betweenEqual(-6, HD, 3) &&
          ShotScore.betweenEqual(-4, IM, 3) &&
          ShotScore.betweenEqual(-3, HF, 4)
        ) {
          result += 8;
          if (isGetMark) this.setMark(result2, "首元の動き", 8);
        } else if (
          ShotScore.betweenEqual(-14, HB, 4) &&
          ShotScore.betweenEqual(-13, TP, 4) &&
          ShotScore.betweenEqual(-8, HD, 5) &&
          ShotScore.betweenEqual(-8, IM, 5) &&
          ShotScore.betweenEqual(-7, HF, 6)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "首元の動き", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "首元の動き", 1);
        }
      }
    );

    // part 7
    this.phase(
      data,
      "右膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(15, AD, 40) &&
          ShotScore.betweenEqual(8, HB, 40) &&
          ShotScore.betweenEqual(2, TP, 40) &&
          ShotScore.betweenEqual(25, HD, 65) &&
          ShotScore.betweenEqual(25, IM, 55) &&
          ShotScore.betweenEqual(20, HF, 50)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "右膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "右膝の角度", 1);
        }
      }
    );

    // part 8
    this.phase(
      data,
      "左膝の角度",
      (
        AD: number,
        HB: number,
        TP: number,
        HD: number,
        IM: number,
        HF: number,
        FN: number
      ) => {
        if (
          ShotScore.betweenEqual(10, AD, 40) &&
          ShotScore.betweenEqual(8, HB, 42) &&
          ShotScore.betweenEqual(14, TP, 52) &&
          ShotScore.betweenEqual(25, HD, 52) &&
          ShotScore.betweenEqual(15, IM, 45) &&
          ShotScore.betweenEqual(12, HF, 45)
        ) {
          result += 4;
          if (isGetMark) this.setMark(result2, "左膝の角度", 4);
        } else {
          result += 1;
          if (isGetMark) this.setMark(result2, "左膝の角度", 1);
        }
      }
    );

    return !isGetMark ? result : result2;
  }

  /*
   *
   */
  public postureScore(shot: any): number {
    let result: number = 0;
    if (shot) {
      const club: number = shot.platform.description.club;
      const data: any = shot.sm;
      switch (club) {
        case 1: //1W
          result = this.postureScoreOf1W(data);
          break;
        case 3: //5W
          result = this.postureScoreOf5W(data);
          break;
        case 10: //7I
          result = this.postureScoreOf7I(data);
          break;
        case 13: //PW
          result = this.postureScoreOfPW(data);
          break;
        default:
      }
    }

    return result;
  }

  /*
   *
   */
  public ballisticScore(shot: any): number {
    let result: number = 0;

    const initSubtypes = (list: any[]): void => {
      list.forEach((element) => {
        // map?
        this.subtypes.push(element.Name);
      });
    };

    if (shot) {
      if (shot.prizm) {
        const list = shot.prizm.DataList;
        if (list) {
          const club = shot.platform.description.club;
          initSubtypes(list);
          switch (this.subtype) {
            case "スコア":
              result = this.calcBallisticScore(list, club);
              break;
            default:
              result = this.valueByName(list, this.subtype);
              break;
          }
        }
      }
    }
    return result;
  }

  // public score(shot: any): number {
  // 	return (this.ballisticScore(shot) + this.postureScore(shot)) / 2;
  // }

  /*
   *
   */
  public comparison(shots: any): number {
    return 0;
  }

  /*
   * set error.
   * for send shotdata to other system.
   * */
  public set_error_cell(club: number, objects: ISkillMonitor): ISkillMonitor {
    const _前傾ライン: IPhase = objects.前傾角度;
    const _センターライン: IPhase = objects.背骨の傾き;
    const _ネックポジション: IPhase = objects.首元の動き;
    const _ライトライン: IPhase = objects.右腰の動き;
    const _レフトライン: IPhase = objects.左腰の動き;
    const _センターポジション: IPhase = objects.重心の動き_正面;
    const _右膝の角度: IPhase = objects.右膝の角度;
    const _左膝の角度: IPhase = objects.左膝の角度;
    // objects.手元の浮き
    // objects.頭の動き
    // objects.両肩の傾き_正面
    // objects.重心の動き_後方
    // objects.グリップ位置
    // objects.手首軌道
    // objects.腰の開き
    // objects.肩の開き

    const 前傾ライン: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const センターポジション: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const センターライン: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const ライトライン: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const レフトライン: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const ネックポジション: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const 右膝の角度: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };
    const 左膝の角度: positionData = {
      AD: 0,
      HB: 0,
      TP: 0,
      HD: 0,
      IM: 0,
      HF: 0,
    };

    前傾ライン.AD = Math.round(_前傾ライン.address.metrics);
    前傾ライン.HB = Math.round(_前傾ライン.backswing.metrics);
    前傾ライン.TP = Math.round(_前傾ライン.top.metrics);
    前傾ライン.HD = Math.round(_前傾ライン.halfdown.metrics);
    前傾ライン.IM = Math.round(_前傾ライン.impact.metrics);
    前傾ライン.HF = Math.round(_前傾ライン.follow.metrics);

    センターポジション.AD = Math.round(_センターポジション.address.metrics);
    センターポジション.HB = Math.round(_センターポジション.backswing.metrics);
    センターポジション.TP = Math.round(_センターポジション.top.metrics);
    センターポジション.HD = Math.round(_センターポジション.halfdown.metrics);
    センターポジション.IM = Math.round(_センターポジション.impact.metrics);
    センターポジション.HF = Math.round(_センターポジション.follow.metrics);

    センターライン.AD = Math.round(_センターライン.address.metrics);
    センターライン.HB = Math.round(_センターライン.backswing.metrics);
    センターライン.TP = Math.round(_センターライン.top.metrics);
    センターライン.HD = Math.round(_センターライン.halfdown.metrics);
    センターライン.IM = Math.round(_センターライン.impact.metrics);
    センターライン.HF = Math.round(_センターライン.follow.metrics);

    ライトライン.AD = Math.round(_ライトライン.address.metrics);
    ライトライン.HB = Math.round(_ライトライン.backswing.metrics);
    ライトライン.TP = Math.round(_ライトライン.top.metrics);
    ライトライン.HD = Math.round(_ライトライン.halfdown.metrics);
    ライトライン.IM = Math.round(_ライトライン.impact.metrics);
    ライトライン.HF = Math.round(_ライトライン.follow.metrics);

    レフトライン.AD = Math.round(_レフトライン.address.metrics);
    レフトライン.HB = Math.round(_レフトライン.backswing.metrics);
    レフトライン.TP = Math.round(_レフトライン.top.metrics);
    レフトライン.HD = Math.round(_レフトライン.halfdown.metrics);
    レフトライン.IM = Math.round(_レフトライン.impact.metrics);
    レフトライン.HF = Math.round(_レフトライン.follow.metrics);

    ネックポジション.AD = Math.round(_ネックポジション.address.metrics);
    ネックポジション.HB = Math.round(_ネックポジション.backswing.metrics);
    ネックポジション.TP = Math.round(_ネックポジション.top.metrics);
    ネックポジション.HD = Math.round(_ネックポジション.halfdown.metrics);
    ネックポジション.IM = Math.round(_ネックポジション.impact.metrics);
    ネックポジション.HF = Math.round(_ネックポジション.follow.metrics);

    右膝の角度.AD = Math.round(_右膝の角度.address.metrics);
    右膝の角度.HB = Math.round(_右膝の角度.backswing.metrics);
    右膝の角度.TP = Math.round(_右膝の角度.top.metrics);
    右膝の角度.HD = Math.round(_右膝の角度.halfdown.metrics);
    右膝の角度.IM = Math.round(_右膝の角度.impact.metrics);
    右膝の角度.HF = Math.round(_右膝の角度.follow.metrics);

    左膝の角度.AD = Math.round(_左膝の角度.address.metrics);
    左膝の角度.HB = Math.round(_左膝の角度.backswing.metrics);
    左膝の角度.TP = Math.round(_左膝の角度.top.metrics);
    左膝の角度.HD = Math.round(_左膝の角度.halfdown.metrics);
    左膝の角度.IM = Math.round(_左膝の角度.impact.metrics);
    左膝の角度.HF = Math.round(_左膝の角度.follow.metrics);

    switch (club) {
      case 1: // 1W
        objects.前傾角度.address.error = false;
        objects.前傾角度.backswing.error =
          前傾ライン.HB < 前傾ライン.AD - 8 ||
          前傾ライン.AD + 5 < 前傾ライン.HB;
        objects.前傾角度.top.error =
          前傾ライン.TP < 前傾ライン.AD - 8 ||
          前傾ライン.AD + 5 < 前傾ライン.TP;
        objects.前傾角度.halfdown.error =
          前傾ライン.HD < 前傾ライン.AD - 10 ||
          前傾ライン.AD + 7 < 前傾ライン.HD;
        objects.前傾角度.impact.error =
          前傾ライン.IM < 前傾ライン.AD - 10 ||
          前傾ライン.AD + 3 < 前傾ライン.IM;
        objects.前傾角度.follow.error =
          前傾ライン.HF < 前傾ライン.AD - 18 ||
          前傾ライン.AD + 3 < 前傾ライン.HF;

        objects.重心の動き_正面.address.error =
          センターポジション.AD < -7 || 0 < センターポジション.AD;
        objects.重心の動き_正面.backswing.error =
          センターポジション.HB < -7 || 0 < センターポジション.HB;
        objects.重心の動き_正面.top.error =
          センターポジション.TP < -9 || 0 < センターポジション.TP;
        objects.重心の動き_正面.halfdown.error =
          センターポジション.HD < -5 || 4 < センターポジション.HD;
        objects.重心の動き_正面.impact.error =
          センターポジション.IM < -4 || 5 < センターポジション.IM;
        objects.重心の動き_正面.follow.error =
          センターポジション.HF < -3 || 5 < センターポジション.HF;

        objects.背骨の傾き.address.error =
          センターライン.AD < -6 || 3 < センターライン.AD;
        objects.背骨の傾き.backswing.error =
          センターライン.HB < -6 || 3 < センターライン.HB;
        objects.背骨の傾き.top.error =
          センターライン.TP < -8 || 3 < センターライン.TP;
        objects.背骨の傾き.halfdown.error =
          センターライン.HD < -12 || 0 < センターライン.HD;
        objects.背骨の傾き.impact.error =
          センターライン.IM < -16 || -3 < センターライン.IM;
        objects.背骨の傾き.follow.error =
          センターライン.HF < -16 || 1 < センターライン.HF;

        objects.右腰の動き.address.error = false;
        objects.右腰の動き.backswing.error =
          ライトライン.HB < ライトライン.AD - 5 ||
          ライトライン.AD + 2 < ライトライン.HB;
        objects.右腰の動き.top.error =
          ライトライン.TP < ライトライン.AD - 5 ||
          ライトライン.AD + 3 < ライトライン.TP;
        objects.右腰の動き.halfdown.error =
          ライトライン.HD < ライトライン.AD - 4 ||
          ライトライン.AD + 8 < ライトライン.HD;
        objects.右腰の動き.impact.error =
          ライトライン.IM < ライトライン.AD + 2 ||
          ライトライン.AD + 10 < ライトライン.IM;
        objects.右腰の動き.follow.error =
          ライトライン.HF < ライトライン.AD + 2 ||
          ライトライン.AD + 20 < ライトライン.HF;

        objects.左腰の動き.address.error = false;
        objects.左腰の動き.backswing.error =
          レフトライン.HB < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.HB;
        objects.左腰の動き.top.error =
          レフトライン.TP < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.TP;
        objects.左腰の動き.halfdown.error =
          レフトライン.HD < レフトライン.AD - 4 ||
          レフトライン.AD + 5 < レフトライン.HD;
        objects.左腰の動き.impact.error =
          レフトライン.IM < レフトライン.AD - 8 ||
          レフトライン.AD + 2 < レフトライン.IM;
        objects.左腰の動き.follow.error =
          レフトライン.HF < レフトライン.AD - 8 ||
          レフトライン.AD + 5 < レフトライン.HF;

        objects.首元の動き.address.error = false;
        objects.首元の動き.backswing.error =
          ネックポジション.HB < -10 || 0 < ネックポジション.HB;
        objects.首元の動き.top.error =
          ネックポジション.TP < -11 || -1 < ネックポジション.TP;
        objects.首元の動き.halfdown.error =
          ネックポジション.HD < -10 || 1 < ネックポジション.HD;
        objects.首元の動き.impact.error =
          ネックポジション.IM < -12 || 2 < ネックポジション.IM;
        objects.首元の動き.follow.error =
          ネックポジション.HF < -9 || 2 < ネックポジション.HF;

        objects.右膝の角度.address.error =
          右膝の角度.AD < 16 || 38 < 右膝の角度.AD;
        objects.右膝の角度.backswing.error =
          右膝の角度.HB < 8 || 40 < 右膝の角度.HB;
        objects.右膝の角度.top.error = 右膝の角度.TP < 2 || 40 < 右膝の角度.TP;
        objects.右膝の角度.halfdown.error =
          右膝の角度.HD < 25 || 65 < 右膝の角度.HD;
        objects.右膝の角度.impact.error =
          右膝の角度.IM < 25 || 55 < 右膝の角度.IM;
        objects.右膝の角度.follow.error =
          右膝の角度.HF < 20 || 50 < 右膝の角度.HF;

        objects.左膝の角度.address.error =
          左膝の角度.AD < 6 || 33 < 左膝の角度.AD;
        objects.左膝の角度.backswing.error =
          左膝の角度.HB < 8 || 38 < 左膝の角度.HB;
        objects.左膝の角度.top.error = 左膝の角度.TP < 14 || 55 < 左膝の角度.TP;
        objects.左膝の角度.halfdown.error =
          左膝の角度.HD < 25 || 58 < 左膝の角度.HD;
        objects.左膝の角度.impact.error =
          左膝の角度.IM < 16 || 45 < 左膝の角度.IM;
        objects.左膝の角度.follow.error =
          左膝の角度.HF < 7 || 35 < 左膝の角度.HF;

        break;

      case 3: // 5W
        objects.前傾角度.address.error = false;
        objects.前傾角度.backswing.error =
          前傾ライン.HB < 前傾ライン.AD - 7 ||
          前傾ライン.AD + 5 < 前傾ライン.HB;
        objects.前傾角度.top.error =
          前傾ライン.TP < 前傾ライン.AD - 7 ||
          前傾ライン.AD + 5 < 前傾ライン.TP;
        objects.前傾角度.halfdown.error =
          前傾ライン.HD < 前傾ライン.AD - 8 ||
          前傾ライン.AD + 7 < 前傾ライン.HD;
        objects.前傾角度.impact.error =
          前傾ライン.IM < 前傾ライン.AD - 8 ||
          前傾ライン.AD + 5 < 前傾ライン.IM;
        objects.前傾角度.follow.error =
          前傾ライン.HF < 前傾ライン.AD - 12 ||
          前傾ライン.AD + 4 < 前傾ライン.HF;

        objects.重心の動き_正面.address.error =
          センターポジション.AD < -7 || 1 < センターポジション.AD;
        objects.重心の動き_正面.backswing.error =
          センターポジション.HB < -7 || 1 < センターポジション.HB;
        objects.重心の動き_正面.top.error =
          センターポジション.TP < -8 || 1 < センターポジション.TP;
        objects.重心の動き_正面.halfdown.error =
          センターポジション.HD < -5 || 3 < センターポジション.HD;
        objects.重心の動き_正面.impact.error =
          センターポジション.IM < -3 || 5 < センターポジション.IM;
        objects.重心の動き_正面.follow.error =
          センターポジション.HF < -2 || 8 < センターポジション.HF;

        objects.背骨の傾き.address.error =
          センターライン.AD < -7 || 3 < センターライン.AD;
        objects.背骨の傾き.backswing.error =
          センターライン.HB < -8 || 2 < センターライン.HB;
        objects.背骨の傾き.top.error =
          センターライン.TP < -8 || 3 < センターライン.TP;
        objects.背骨の傾き.halfdown.error =
          センターライン.HD < -8 || 2 < センターライン.HD;
        objects.背骨の傾き.impact.error =
          センターライン.IM < -12 || -1 < センターライン.IM;
        objects.背骨の傾き.follow.error =
          センターライン.HF < -14 || 1 < センターライン.HF;

        objects.右腰の動き.address.error = false;
        objects.右腰の動き.backswing.error =
          ライトライン.HB < ライトライン.AD - 5 ||
          ライトライン.AD + 2 < ライトライン.HB;
        objects.右腰の動き.top.error =
          ライトライン.TP < ライトライン.AD - 5 ||
          ライトライン.AD + 3 < ライトライン.TP;
        objects.右腰の動き.halfdown.error =
          ライトライン.HD < ライトライン.AD - 5 ||
          ライトライン.AD + 8 < ライトライン.HD;
        objects.右腰の動き.impact.error =
          ライトライン.IM < ライトライン.AD + 2 ||
          ライトライン.AD + 9 < ライトライン.IM;
        objects.右腰の動き.follow.error =
          ライトライン.HF < ライトライン.AD + 2 ||
          ライトライン.AD + 20 < ライトライン.HF;

        objects.左腰の動き.address.error = false;
        objects.左腰の動き.backswing.error =
          レフトライン.HB < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.HB;
        objects.左腰の動き.top.error =
          レフトライン.TP < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.TP;
        objects.左腰の動き.halfdown.error =
          レフトライン.HD < レフトライン.AD - 4 ||
          レフトライン.AD + 3 < レフトライン.HD;
        objects.左腰の動き.impact.error =
          レフトライン.IM < レフトライン.AD - 8 ||
          レフトライン.AD + 2 < レフトライン.IM;
        objects.左腰の動き.follow.error =
          レフトライン.HF < レフトライン.AD - 8 ||
          レフトライン.AD + 5 < レフトライン.HF;

        objects.首元の動き.address.error = false;
        objects.首元の動き.backswing.error =
          ネックポジション.HB < -10 || 0 < ネックポジション.HB;
        objects.首元の動き.top.error =
          ネックポジション.TP < -9 || 0 < ネックポジション.TP;
        objects.首元の動き.halfdown.error =
          ネックポジション.HD < -6 || 3 < ネックポジション.HD;
        objects.首元の動き.impact.error =
          ネックポジション.IM < -4 || 3 < ネックポジション.IM;
        objects.首元の動き.follow.error =
          ネックポジション.HF < -3 || 4 < ネックポジション.HF;

        objects.右膝の角度.address.error =
          右膝の角度.AD < 15 || 40 < 右膝の角度.AD;
        objects.右膝の角度.backswing.error =
          右膝の角度.HB < 8 || 40 < 右膝の角度.HB;
        objects.右膝の角度.top.error = 右膝の角度.TP < 2 || 40 < 右膝の角度.TP;
        objects.右膝の角度.halfdown.error =
          右膝の角度.HD < 25 || 65 < 右膝の角度.HD;
        objects.右膝の角度.impact.error =
          右膝の角度.IM < 25 || 55 < 右膝の角度.IM;
        objects.右膝の角度.follow.error =
          右膝の角度.HF < 20 || 50 < 右膝の角度.HF;

        objects.左膝の角度.address.error =
          左膝の角度.AD < 10 || 40 < 左膝の角度.AD;
        objects.左膝の角度.backswing.error =
          左膝の角度.HB < 8 || 42 < 左膝の角度.HB;
        objects.左膝の角度.top.error = 左膝の角度.TP < 14 || 52 < 左膝の角度.TP;
        objects.左膝の角度.halfdown.error =
          左膝の角度.HD < 25 || 52 < 左膝の角度.HD;
        objects.左膝の角度.impact.error =
          左膝の角度.IM < 15 || 45 < 左膝の角度.IM;
        objects.左膝の角度.follow.error =
          左膝の角度.HF < 12 || 45 < 左膝の角度.HF;

        break;

      case 10: // 7I
        objects.前傾角度.address.error = false;
        objects.前傾角度.backswing.error =
          前傾ライン.HB < 前傾ライン.AD - 6 ||
          前傾ライン.AD + 5 < 前傾ライン.HB;
        objects.前傾角度.top.error =
          前傾ライン.TP < 前傾ライン.AD - 6 ||
          前傾ライン.AD + 5 < 前傾ライン.TP;
        objects.前傾角度.halfdown.error =
          前傾ライン.HD < 前傾ライン.AD - 6 ||
          前傾ライン.AD + 7 < 前傾ライン.HD;
        objects.前傾角度.impact.error =
          前傾ライン.IM < 前傾ライン.AD - 7 ||
          前傾ライン.AD + 5 < 前傾ライン.IM;
        objects.前傾角度.follow.error =
          前傾ライン.HF < 前傾ライン.AD - 13 ||
          前傾ライン.AD + 4 < 前傾ライン.HF;

        objects.重心の動き_正面.address.error =
          センターポジション.AD < -7 || 1 < センターポジション.AD;
        objects.重心の動き_正面.backswing.error =
          センターポジション.HB < -7 || 1 < センターポジション.HB;
        objects.重心の動き_正面.top.error =
          センターポジション.TP < -8 || 1 < センターポジション.TP;
        objects.重心の動き_正面.halfdown.error =
          センターポジション.HD < -4 || 5 < センターポジション.HD;
        objects.重心の動き_正面.impact.error =
          センターポジション.IM < -1 || 6 < センターポジション.IM;
        objects.重心の動き_正面.follow.error =
          センターポジション.HF < 0 || 13 < センターポジション.HF;

        objects.背骨の傾き.address.error =
          センターライン.AD < -7 || 3 < センターライン.AD;
        objects.背骨の傾き.backswing.error =
          センターライン.HB < -8 || 2 < センターライン.HB;
        objects.背骨の傾き.top.error =
          センターライン.TP < -8 || 3 < センターライン.TP;
        objects.背骨の傾き.halfdown.error =
          センターライン.HD < -8 || 2 < センターライン.HD;
        objects.背骨の傾き.impact.error =
          センターライン.IM < -12 || -1 < センターライン.IM;
        objects.背骨の傾き.follow.error =
          センターライン.HF < -14 || 1 < センターライン.HF;

        objects.右腰の動き.address.error = false;
        objects.右腰の動き.backswing.error =
          ライトライン.HB < ライトライン.AD - 5 ||
          ライトライン.AD + 2 < ライトライン.HB;
        objects.右腰の動き.top.error =
          ライトライン.TP < ライトライン.AD - 5 ||
          ライトライン.AD + 3 < ライトライン.TP;
        objects.右腰の動き.halfdown.error =
          ライトライン.HD < ライトライン.AD - 5 ||
          ライトライン.AD + 8 < ライトライン.HD;
        objects.右腰の動き.impact.error =
          ライトライン.IM < ライトライン.AD + 2 ||
          ライトライン.AD + 9 < ライトライン.IM;
        objects.右腰の動き.follow.error =
          ライトライン.HF < ライトライン.AD + 2 ||
          ライトライン.AD + 20 < ライトライン.HF;

        objects.左腰の動き.address.error = false;
        objects.左腰の動き.backswing.error =
          レフトライン.HB < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.HB;
        objects.左腰の動き.top.error =
          レフトライン.TP < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.TP;
        objects.左腰の動き.halfdown.error =
          レフトライン.HD < レフトライン.AD - 4 ||
          レフトライン.AD + 3 < レフトライン.HD;
        objects.左腰の動き.impact.error =
          レフトライン.IM < レフトライン.AD - 8 ||
          レフトライン.AD + 2 < レフトライン.IM;
        objects.左腰の動き.follow.error =
          レフトライン.HF < レフトライン.AD - 8 ||
          レフトライン.AD + 5 < レフトライン.HF;

        objects.首元の動き.address.error = false;
        objects.首元の動き.backswing.error =
          ネックポジション.HB < -10 || 0 < ネックポジション.HB;
        objects.首元の動き.top.error =
          ネックポジション.TP < -9 || 0 < ネックポジション.TP;
        objects.首元の動き.halfdown.error =
          ネックポジション.HD < -6 || 3 < ネックポジション.HD;
        objects.首元の動き.impact.error =
          ネックポジション.IM < -4 || 3 < ネックポジション.IM;
        objects.首元の動き.follow.error =
          ネックポジション.HF < -3 || 4 < ネックポジション.HF;

        objects.右膝の角度.address.error =
          右膝の角度.AD < 15 || 40 < 右膝の角度.AD;
        objects.右膝の角度.backswing.error =
          右膝の角度.HB < 8 || 40 < 右膝の角度.HB;
        objects.右膝の角度.top.error = 右膝の角度.TP < 2 || 40 < 右膝の角度.TP;
        objects.右膝の角度.halfdown.error =
          右膝の角度.HD < 25 || 65 < 右膝の角度.HD;
        objects.右膝の角度.impact.error =
          右膝の角度.IM < 25 || 55 < 右膝の角度.IM;
        objects.右膝の角度.follow.error =
          右膝の角度.HF < 20 || 50 < 右膝の角度.HF;

        objects.左膝の角度.address.error =
          左膝の角度.AD < 10 || 40 < 左膝の角度.AD;
        objects.左膝の角度.backswing.error =
          左膝の角度.HB < 8 || 42 < 左膝の角度.HB;
        objects.左膝の角度.top.error = 左膝の角度.TP < 14 || 52 < 左膝の角度.TP;
        objects.左膝の角度.halfdown.error =
          左膝の角度.HD < 25 || 52 < 左膝の角度.HD;
        objects.左膝の角度.impact.error =
          左膝の角度.IM < 15 || 45 < 左膝の角度.IM;
        objects.左膝の角度.follow.error =
          左膝の角度.HF < 12 || 45 < 左膝の角度.HF;

        break;

      case 13: // PW
        // todo: score
        /*
				objects.前傾角度.address.error = false;
				objects.前傾角度.backswing.error = ((前傾ライン.HB < 前傾ライン.AD - 4) || (前傾ライン.AD + 3 < 前傾ライン.HB));
				objects.前傾角度.top.error = ((前傾ライン.TP < 前傾ライン.AD - 4) || (前傾ライン.AD + 3 < 前傾ライン.TP));
				objects.前傾角度.halfdown.error = ((前傾ライン.HD < 前傾ライン.AD - 4) || (前傾ライン.AD + 4 < 前傾ライン.HD));
				objects.前傾角度.impact.error = ((前傾ライン.IM < 前傾ライン.AD - 4) || (前傾ライン.AD + 4 < 前傾ライン.IM));
				objects.前傾角度.follow.error = ((前傾ライン.HF < 前傾ライン.AD - 13) || (前傾ライン.AD + 4 < 前傾ライン.HF));
*/
        objects.前傾角度.address.error = false;
        objects.前傾角度.backswing.error =
          前傾ライン.HB < 前傾ライン.AD - 6 ||
          前傾ライン.AD + 5 < 前傾ライン.HB;
        objects.前傾角度.top.error =
          前傾ライン.TP < 前傾ライン.AD - 6 ||
          前傾ライン.AD + 5 < 前傾ライン.TP;
        objects.前傾角度.halfdown.error =
          前傾ライン.HD < 前傾ライン.AD - 6 ||
          前傾ライン.AD + 7 < 前傾ライン.HD;
        objects.前傾角度.impact.error =
          前傾ライン.IM < 前傾ライン.AD - 7 ||
          前傾ライン.AD + 5 < 前傾ライン.IM;
        objects.前傾角度.follow.error =
          前傾ライン.HF < 前傾ライン.AD - 13 ||
          前傾ライン.AD + 4 < 前傾ライン.HF;

        objects.重心の動き_正面.address.error =
          センターポジション.AD < -5 || 2 < センターポジション.AD;
        objects.重心の動き_正面.backswing.error =
          センターポジション.HB < -5 || 2 < センターポジション.HB;
        objects.重心の動き_正面.top.error =
          センターポジション.TP < -6 || 2 < センターポジション.TP;
        objects.重心の動き_正面.halfdown.error =
          センターポジション.HD < -4 || 4 < センターポジション.HD;
        objects.重心の動き_正面.impact.error =
          センターポジション.IM < -1 || 7 < センターポジション.IM;
        objects.重心の動き_正面.follow.error =
          センターポジション.HF < 0 || 10 < センターポジション.HF;

        objects.背骨の傾き.address.error =
          センターライン.AD < -6 || 3 < センターライン.AD;
        objects.背骨の傾き.backswing.error =
          センターライン.HB < -6 || 3 < センターライン.HB;
        objects.背骨の傾き.top.error =
          センターライン.TP < -6 || 3 < センターライン.TP;
        objects.背骨の傾き.halfdown.error =
          センターライン.HD < -6 || 3 < センターライン.HD;
        objects.背骨の傾き.impact.error =
          センターライン.IM < -8 || -3 < センターライン.IM;
        objects.背骨の傾き.follow.error =
          センターライン.HF < -10 || 3 < センターライン.HF;
        // todo: score
        /*
				objects.右腰の動き.address.error = false;
				objects.右腰の動き.backswing.error = ((ライトライン.HB < ライトライン.AD - 4) || (ライトライン.AD + 3 < ライトライン.HB));
				objects.右腰の動き.top.error = ((ライトライン.TP < ライトライン.AD - 4) || (ライトライン.AD + 4 < ライトライン.TP));
				objects.右腰の動き.halfdown.error = ((ライトライン.HD < ライトライン.AD - 5) || (ライトライン.AD + 8 < ライトライン.HD));
				objects.右腰の動き.impact.error = ((ライトライン.IM < ライトライン.AD + 2) || (ライトライン.AD + 9 < ライトライン.IM));
				objects.右腰の動き.follow.error = ((ライトライン.HF < ライトライン.AD + 2) || (ライトライン.AD + 20 < ライトライン.HF));

				objects.左腰の動き.address.error = false;
				objects.左腰の動き.backswing.error = ((レフトライン.HB < レフトライン.AD) || (レフトライン.AD + 12 < レフトライン.HB));
				objects.左腰の動き.top.error = ((レフトライン.TP < レフトライン.AD) || (レフトライン.AD + 12 < レフトライン.TP));
				objects.左腰の動き.halfdown.error = ((レフトライン.HD < レフトライン.AD - 5) || (レフトライン.AD + 2 < レフトライン.HD));
				objects.左腰の動き.impact.error = ((レフトライン.IM < レフトライン.AD - 6) || (レフトライン.AD + 1 < レフトライン.IM));
				objects.左腰の動き.follow.error = ((レフトライン.HF < レフトライン.AD - 6) || (レフトライン.AD + 5 < レフトライン.HF));
*/
        objects.右腰の動き.address.error = false;
        objects.右腰の動き.backswing.error =
          ライトライン.HB < ライトライン.AD - 5 ||
          ライトライン.AD + 2 < ライトライン.HB;
        objects.右腰の動き.top.error =
          ライトライン.TP < ライトライン.AD - 5 ||
          ライトライン.AD + 3 < ライトライン.TP;
        objects.右腰の動き.halfdown.error =
          ライトライン.HD < ライトライン.AD - 5 ||
          ライトライン.AD + 8 < ライトライン.HD;
        objects.右腰の動き.impact.error =
          ライトライン.IM < ライトライン.AD + 2 ||
          ライトライン.AD + 9 < ライトライン.IM;
        objects.右腰の動き.follow.error =
          ライトライン.HF < ライトライン.AD + 2 ||
          ライトライン.AD + 20 < ライトライン.HF;

        objects.左腰の動き.address.error = false;
        objects.左腰の動き.backswing.error =
          レフトライン.HB < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.HB;
        objects.左腰の動き.top.error =
          レフトライン.TP < レフトライン.AD ||
          レフトライン.AD + 12 < レフトライン.TP;
        objects.左腰の動き.halfdown.error =
          レフトライン.HD < レフトライン.AD - 4 ||
          レフトライン.AD + 3 < レフトライン.HD;
        objects.左腰の動き.impact.error =
          レフトライン.IM < レフトライン.AD - 8 ||
          レフトライン.AD + 2 < レフトライン.IM;
        objects.左腰の動き.follow.error =
          レフトライン.HF < レフトライン.AD - 8 ||
          レフトライン.AD + 5 < レフトライン.HF;

        objects.首元の動き.address.error = false;
        objects.首元の動き.backswing.error =
          ネックポジション.HB < -10 || 0 < ネックポジション.HB;
        objects.首元の動き.top.error =
          ネックポジション.TP < -9 || 0 < ネックポジション.TP;
        objects.首元の動き.halfdown.error =
          ネックポジション.HD < -6 || 3 < ネックポジション.HD;
        objects.首元の動き.impact.error =
          ネックポジション.IM < -4 || 3 < ネックポジション.IM;
        objects.首元の動き.follow.error =
          ネックポジション.HF < -3 || 4 < ネックポジション.HF;

        objects.右膝の角度.address.error =
          右膝の角度.AD < 15 || 40 < 右膝の角度.AD;
        objects.右膝の角度.backswing.error =
          右膝の角度.HB < 8 || 40 < 右膝の角度.HB;
        objects.右膝の角度.top.error = 右膝の角度.TP < 2 || 40 < 右膝の角度.TP;
        objects.右膝の角度.halfdown.error =
          右膝の角度.HD < 25 || 65 < 右膝の角度.HD;
        objects.右膝の角度.impact.error =
          右膝の角度.IM < 25 || 55 < 右膝の角度.IM;
        objects.右膝の角度.follow.error =
          右膝の角度.HF < 20 || 50 < 右膝の角度.HF;

        objects.左膝の角度.address.error =
          左膝の角度.AD < 10 || 40 < 左膝の角度.AD;
        objects.左膝の角度.backswing.error =
          左膝の角度.HB < 8 || 42 < 左膝の角度.HB;
        objects.左膝の角度.top.error = 左膝の角度.TP < 14 || 52 < 左膝の角度.TP;
        objects.左膝の角度.halfdown.error =
          左膝の角度.HD < 25 || 52 < 左膝の角度.HD;
        objects.左膝の角度.impact.error =
          左膝の角度.IM < 15 || 45 < 左膝の角度.IM;
        objects.左膝の角度.follow.error =
          左膝の角度.HF < 12 || 45 < 左膝の角度.HF;

        break;

      default:
    }
    return objects;
  }

  /**
   * @returns error
   */
  public is_error(club: number, line: any[], calc: string): boolean {
    let AD = Math.round(line[1].metrics);
    let HB = Math.round(line[2].metrics);
    let TP = Math.round(line[3].metrics);
    let HD = Math.round(line[4].metrics);
    let IM = Math.round(line[5].metrics);
    let HF = Math.round(line[6].metrics);

    switch (calc) {
      case "average":
        AD = Math.round(line[1].avarage);
        HB = Math.round(line[2].avarage);
        TP = Math.round(line[3].avarage);
        HD = Math.round(line[4].avarage);
        IM = Math.round(line[5].avarage);
        HF = Math.round(line[6].avarage);
        break;
      default:
    }
    let error: boolean = false;

    switch (club) {
      case 1: // 1W
        switch (line[0]) {
          case "前傾ライン":
            error =
              HB < AD - 8 ||
              AD + 5 < HB ||
              TP < AD - 8 ||
              AD + 5 < TP ||
              HD < AD - 10 ||
              AD + 7 < HD ||
              IM < AD - 10 ||
              AD + 3 < IM ||
              HF < AD - 18 ||
              AD + 3 < HF;
            break;
          case "センターポジション":
            error =
              AD < -7 ||
              0 < AD ||
              HB < -7 ||
              0 < HB ||
              TP < -9 ||
              0 < TP ||
              HD < -5 ||
              4 < HD ||
              IM < -4 ||
              5 < IM ||
              HF < -3 ||
              5 < HF;
            break;
          case "センターライン":
            error =
              AD < -6 ||
              3 < AD ||
              HB < -6 ||
              3 < HB ||
              TP < -8 ||
              3 < TP ||
              HD < -12 ||
              0 < HD ||
              IM < -16 ||
              -3 < IM ||
              HF < -16 ||
              1 < HF;
            break;
          case "ライトライン":
            error =
              HB < AD - 5 ||
              AD + 2 < HB ||
              TP < AD - 5 ||
              AD + 3 < TP ||
              HD < AD - 4 ||
              AD + 8 < HD ||
              IM < AD + 2 ||
              AD + 10 < IM ||
              HF < AD + 2 ||
              AD + 20 < HF;
            break;
          case "レフトライン":
            error =
              HB < AD ||
              AD + 12 < HB ||
              TP < AD ||
              AD + 12 < TP ||
              HD < AD - 4 ||
              AD + 5 < HD ||
              IM < AD - 8 ||
              AD + 2 < IM ||
              HF < AD - 8 ||
              AD + 5 < HF;
            break;
          case "ネックポジション":
            error =
              HB < -10 ||
              0 < HB ||
              TP < -11 ||
              -1 < TP ||
              HD < -10 ||
              1 < HD ||
              IM < -12 ||
              2 < IM ||
              HF < -9 ||
              2 < HF;
            break;
          case "右膝の角度":
            error =
              AD < 16 ||
              38 < AD ||
              HB < 8 ||
              40 < HB ||
              TP < 2 ||
              40 < TP ||
              HD < 25 ||
              65 < HD ||
              IM < 25 ||
              55 < IM ||
              HF < 20 ||
              50 < HF;
            break;
          case "左膝の角度":
            error =
              AD < 6 ||
              33 < AD ||
              HB < 8 ||
              38 < HB ||
              TP < 14 ||
              55 < TP ||
              HD < 25 ||
              58 < HD ||
              IM < 16 ||
              45 < IM ||
              HF < 7 ||
              35 < HF;
            break;
          default:
            break;
        }
        break;
      case 3: // 5W
        switch (line[0]) {
          case "前傾ライン":
            error =
              HB < AD - 7 ||
              AD + 5 < HB ||
              TP < AD - 7 ||
              AD + 5 < TP ||
              HD < AD - 8 ||
              AD + 7 < HD ||
              IM < AD - 8 ||
              AD + 5 < IM ||
              HF < AD - 12 ||
              AD + 4 < HF;
            break;
          case "センターポジション":
            error =
              AD < -7 ||
              1 < AD ||
              HB < -7 ||
              1 < HB ||
              TP < -8 ||
              1 < TP ||
              HD < -5 ||
              3 < HD ||
              IM < -3 ||
              5 < IM ||
              HF < -2 ||
              8 < HF;
            break;
          case "センターライン":
            error =
              AD < -7 ||
              3 < AD ||
              HB < -8 ||
              2 < HB ||
              TP < -8 ||
              3 < TP ||
              HD < -8 ||
              2 < HD ||
              IM < -12 ||
              -1 < IM ||
              HF < -14 ||
              1 < HF;
            break;
          case "ライトライン":
            error =
              HB < AD - 5 ||
              AD + 2 < HB ||
              TP < AD - 5 ||
              AD + 3 < TP ||
              HD < AD - 5 ||
              AD + 8 < HD ||
              IM < AD + 2 ||
              AD + 9 < IM ||
              HF < AD + 2 ||
              AD + 20 < HF;
            break;
          case "レフトライン":
            error =
              HB < AD ||
              AD + 12 < HB ||
              TP < AD ||
              AD + 12 < TP ||
              HD < AD - 4 ||
              AD + 3 < HD ||
              IM < AD - 8 ||
              AD + 2 < IM ||
              HF < AD - 8 ||
              AD + 5 < HF;
            break;
          case "ネックポジション":
            error =
              HB < -10 ||
              0 < HB ||
              TP < -9 ||
              0 < TP ||
              HD < -6 ||
              3 < HD ||
              IM < -4 ||
              3 < IM ||
              HF < -3 ||
              4 < HF;
            break;
          case "右膝の角度":
            error =
              AD < 15 ||
              40 < AD ||
              HB < 8 ||
              40 < HB ||
              TP < 2 ||
              40 < TP ||
              HD < 25 ||
              65 < HD ||
              IM < 25 ||
              55 < IM ||
              HF < 20 ||
              50 < HF;
            break;
          case "左膝の角度":
            error =
              AD < 10 ||
              40 < AD ||
              HB < 8 ||
              42 < HB ||
              TP < 14 ||
              52 < TP ||
              HD < 25 ||
              52 < HD ||
              IM < 15 ||
              45 < IM ||
              HF < 12 ||
              45 < HF;
            break;
          default:
            break;
        }
        break;
      case 10: // 7I
        switch (line[0]) {
          case "前傾ライン":
            error =
              HB < AD - 6 ||
              AD + 5 < HB ||
              TP < AD - 6 ||
              AD + 5 < TP ||
              HD < AD - 6 ||
              AD + 7 < HD ||
              IM < AD - 7 ||
              AD + 5 < IM ||
              HF < AD - 13 ||
              AD + 4 < HF;
            break;
          case "センターポジション":
            error =
              AD < -7 ||
              1 < AD ||
              HB < -7 ||
              1 < HB ||
              TP < -8 ||
              1 < TP ||
              HD < -4 ||
              5 < HD ||
              IM < -1 ||
              6 < IM ||
              HF < 0 ||
              13 < HF;
            break;
          case "センターライン":
            error =
              AD < -7 ||
              3 < AD ||
              HB < -8 ||
              2 < HB ||
              TP < -8 ||
              3 < TP ||
              HD < -8 ||
              2 < HD ||
              IM < -12 ||
              -1 < IM ||
              HF < -14 ||
              1 < HF;
            break;
          case "ライトライン":
            error =
              HB < AD - 5 ||
              AD + 2 < HB ||
              TP < AD - 5 ||
              AD + 3 < TP ||
              HD < AD - 5 ||
              AD + 8 < HD ||
              IM < AD + 2 ||
              AD + 9 < IM ||
              HF < AD + 2 ||
              AD + 20 < HF;
            break;
          case "レフトライン":
            error =
              HB < AD ||
              AD + 12 < HB ||
              TP < AD ||
              AD + 12 < TP ||
              HD < AD - 4 ||
              AD + 3 < HD ||
              IM < AD - 8 ||
              AD + 2 < IM ||
              HF < AD - 8 ||
              AD + 5 < HF;
            break;
          case "ネックポジション":
            error =
              HB < -10 ||
              0 < HB ||
              TP < -9 ||
              0 < TP ||
              HD < -6 ||
              3 < HD ||
              IM < -4 ||
              3 < IM ||
              HF < -3 ||
              4 < HF;
            break;
          case "右膝の角度":
            error =
              AD < 15 ||
              40 < AD ||
              HB < 8 ||
              40 < HB ||
              TP < 2 ||
              40 < TP ||
              HD < 25 ||
              65 < HD ||
              IM < 25 ||
              55 < IM ||
              HF < 20 ||
              50 < HF;
            break;
          case "左膝の角度":
            error =
              AD < 10 ||
              40 < AD ||
              HB < 8 ||
              42 < HB ||
              TP < 14 ||
              52 < TP ||
              HD < 25 ||
              52 < HD ||
              IM < 15 ||
              45 < IM ||
              HF < 12 ||
              45 < HF;
            break;
          default:
            break;
        }
        break;
      case 13: // PW
        switch (line[0]) {
          // todo: score
          /*
					case "前傾ライン":
						error = (((HB < AD - 4) || (AD + 3 < HB)) ||
							((TP < AD - 4) || (AD + 3 < TP)) ||
							((HD < AD - 4) || (AD + 4 < HD)) ||
							((IM < AD - 4) || (AD + 4 < IM)) ||
							((HF < AD - 13) || (AD + 4 < HF)));
						break;
						*/
          case "前傾ライン":
            error =
              HB < AD - 6 ||
              AD + 5 < HB ||
              TP < AD - 6 ||
              AD + 5 < TP ||
              HD < AD - 6 ||
              AD + 7 < HD ||
              IM < AD - 7 ||
              AD + 5 < IM ||
              HF < AD - 13 ||
              AD + 4 < HF;
            break;
          case "センターポジション":
            error =
              AD < -5 ||
              2 < AD ||
              HB < -5 ||
              2 < HB ||
              TP < -6 ||
              2 < TP ||
              HD < -4 ||
              4 < HD ||
              IM < -1 ||
              7 < IM ||
              HF < 0 ||
              10 < HF;
            break;
          case "センターライン":
            error =
              AD < -6 ||
              3 < AD ||
              HB < -6 ||
              3 < HB ||
              TP < -6 ||
              3 < TP ||
              HD < -6 ||
              3 < HD ||
              IM < -8 ||
              -3 < IM ||
              HF < -10 ||
              3 < HF;
            break;
          // todo: score
          /*
				case "ライトライン":
					error = (((HB < AD - 4) || (AD + 3 < HB)) ||
						((TP < AD - 4) || (AD + 4 < TP)) ||
						((HD < AD - 5) || (AD + 8 < HD)) ||
						((IM < AD + 2) || (AD + 9 < IM)) ||
						((HF < AD + 2) || (AD + 20 < HF)));
					break;
				case "レフトライン":
					error = (((HB < AD) || (AD + 12 < HB)) ||
						((TP < AD) || (AD + 12 < TP)) ||
						((HD < AD - 5) || (AD + 2 < HD)) ||
						((IM < AD - 6) || (AD + 1 < IM)) ||
						((HF < AD - 6) || (AD + 5 < HF)));
					break;
					*/
          case "ライトライン":
            error =
              HB < AD - 5 ||
              AD + 2 < HB ||
              TP < AD - 5 ||
              AD + 3 < TP ||
              HD < AD - 5 ||
              AD + 8 < HD ||
              IM < AD + 2 ||
              AD + 9 < IM ||
              HF < AD + 2 ||
              AD + 20 < HF;
            break;
          case "レフトライン":
            error =
              HB < AD ||
              AD + 12 < HB ||
              TP < AD ||
              AD + 12 < TP ||
              HD < AD - 4 ||
              AD + 3 < HD ||
              IM < AD - 8 ||
              AD + 2 < IM ||
              HF < AD - 8 ||
              AD + 5 < HF;
            break;
          case "ネックポジション":
            error =
              HB < -10 ||
              0 < HB ||
              TP < -9 ||
              0 < TP ||
              HD < -6 ||
              3 < HD ||
              IM < -4 ||
              3 < IM ||
              HF < -3 ||
              4 < HF;
            break;
          case "右膝の角度":
            error =
              AD < 15 ||
              40 < AD ||
              HB < 8 ||
              40 < HB ||
              TP < 2 ||
              40 < TP ||
              HD < 25 ||
              65 < HD ||
              IM < 25 ||
              55 < IM ||
              HF < 20 ||
              50 < HF;
            break;
          case "左膝の角度":
            error =
              AD < 10 ||
              40 < AD ||
              HB < 8 ||
              42 < HB ||
              TP < 14 ||
              52 < TP ||
              HD < 25 ||
              52 < HD ||
              IM < 15 ||
              45 < IM ||
              HF < 12 ||
              45 < HF;
            break;
          default:
            break;
        }
        break;
      default:
    }
    return error;
  }

  // todo: judgement by cells.
  /**
   * @returns error
   */
  public is_error_cell(
    club: number,
    line: any[],
    columns: number,
    calc: string
  ): boolean {
    enum column_name {
      ad = 1,
      hb = 2,
      tp = 3,
      hd = 4,
      im = 5,
      hf = 6,
    }

    let AD = Math.round(line[1].metrics);
    let HB = Math.round(line[2].metrics);
    let TP = Math.round(line[3].metrics);
    let HD = Math.round(line[4].metrics);
    let IM = Math.round(line[5].metrics);
    let HF = Math.round(line[6].metrics);

    switch (calc) {
      case "average":
        AD = Math.round(line[1].avarage);
        HB = Math.round(line[2].avarage);
        TP = Math.round(line[3].avarage);
        HD = Math.round(line[4].avarage);
        IM = Math.round(line[5].avarage);
        HF = Math.round(line[6].avarage);
        break;
      default:
    }
    let error: boolean = false;

    switch (club) {
      case 1: // 1W
        switch (line[0]) {
          case "前傾ライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 8 || AD + 5 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 8 || AD + 5 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 10 || AD + 7 < HD;
                break;
              case column_name.im:
                error = IM < AD - 10 || AD + 3 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 18 || AD + 3 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターポジション":
            switch (columns) {
              case column_name.ad:
                error = AD < -7 || 0 < AD;
                break;
              case column_name.hb:
                error = HB < -7 || 0 < HB;
                break;
              case column_name.tp:
                error = TP < -9 || 0 < TP;
                break;
              case column_name.hd:
                error = HD < -5 || 4 < HD;
                break;
              case column_name.im:
                error = IM < -4 || 5 < IM;
                break;
              case column_name.hf:
                error = HF < -3 || 5 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターライン":
            switch (columns) {
              case column_name.ad:
                error = AD < -6 || 3 < AD;
                break;
              case column_name.hb:
                error = HB < -6 || 3 < HB;
                break;
              case column_name.tp:
                error = TP < -8 || 3 < TP;
                break;
              case column_name.hd:
                error = HD < -12 || 0 < HD;
                break;
              case column_name.im:
                error = IM < -16 || -3 < IM;
                break;
              case column_name.hf:
                error = HF < -16 || 1 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ライトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 5 || AD + 2 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 5 || AD + 3 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 4 || AD + 8 < HD;
                break;
              case column_name.im:
                error = IM < AD + 2 || AD + 10 < IM;
                break;
              case column_name.hf:
                error = HF < AD + 2 || AD + 20 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "レフトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD || AD + 12 < HB;
                break;
              case column_name.tp:
                error = TP < AD || AD + 12 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 4 || AD + 5 < HD;
                break;
              case column_name.im:
                error = IM < AD - 8 || AD + 2 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 8 || AD + 5 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ネックポジション":
            switch (columns) {
              case column_name.hb:
                error = HB < -10 || 0 < HB;
                break;
              case column_name.tp:
                error = TP < -11 || -1 < TP;
                break;
              case column_name.hd:
                error = HD < -10 || 1 < HD;
                break;
              case column_name.im:
                error = IM < -12 || 2 < IM;
                break;
              case column_name.hf:
                error = HF < -9 || 2 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "右膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 16 || 38 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 40 < HB;
                break;
              case column_name.tp:
                error = TP < 2 || 40 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 65 < HD;
                break;
              case column_name.im:
                error = IM < 25 || 55 < IM;
                break;
              case column_name.hf:
                error = HF < 20 || 50 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "左膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 6 || 33 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 38 < HB;
                break;
              case column_name.tp:
                error = TP < 14 || 55 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 58 < HD;
                break;
              case column_name.im:
                error = IM < 16 || 45 < IM;
                break;
              case column_name.hf:
                error = HF < 7 || 35 < HF;
                break;
              default:
                error = false;
            }
            break;
          default:
            break;
        }
        break;
      case 3: // 5W
        switch (line[0]) {
          case "前傾ライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 7 || AD + 5 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 7 || AD + 5 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 8 || AD + 7 < HD;
                break;
              case column_name.im:
                error = IM < AD - 8 || AD + 5 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 12 || AD + 4 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターポジション":
            switch (columns) {
              case column_name.ad:
                error = AD < -7 || 1 < AD;
                break;
              case column_name.hb:
                error = HB < -7 || 1 < HB;
                break;
              case column_name.tp:
                error = TP < -8 || 1 < TP;
                break;
              case column_name.hd:
                error = HD < -5 || 3 < HD;
                break;
              case column_name.im:
                error = IM < -3 || 5 < IM;
                break;
              case column_name.hf:
                error = HF < -2 || 8 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターライン":
            switch (columns) {
              case column_name.ad:
                error = AD < -7 || 3 < AD;
                break;
              case column_name.hb:
                error = HB < -8 || 2 < HB;
                break;
              case column_name.tp:
                error = TP < -8 || 3 < TP;
                break;
              case column_name.hd:
                error = HD < -8 || 2 < HD;
                break;
              case column_name.im:
                error = IM < -12 || -1 < IM;
                break;
              case column_name.hf:
                error = HF < -14 || 1 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ライトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 5 || AD + 2 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 5 || AD + 3 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 5 || AD + 8 < HD;
                break;
              case column_name.im:
                error = IM < AD + 2 || AD + 9 < IM;
                break;
              case column_name.hf:
                error = HF < AD + 2 || AD + 20 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "レフトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD || AD + 12 < HB;
                break;
              case column_name.tp:
                error = TP < AD || AD + 12 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 4 || AD + 3 < HD;
                break;
              case column_name.im:
                error = IM < AD - 8 || AD + 2 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 8 || AD + 5 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ネックポジション":
            switch (columns) {
              case column_name.hb:
                error = HB < -10 || 0 < HB;
                break;
              case column_name.tp:
                error = TP < -9 || 0 < TP;
                break;
              case column_name.hd:
                error = HD < -6 || 3 < HD;
                break;
              case column_name.im:
                error = IM < -4 || 3 < IM;
                break;
              case column_name.hf:
                error = HF < -3 || 4 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "右膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 15 || 40 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 40 < HB;
                break;
              case column_name.tp:
                error = TP < 2 || 40 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 65 < HD;
                break;
              case column_name.im:
                error = IM < 25 || 55 < IM;
                break;
              case column_name.hf:
                error = HF < 20 || 50 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "左膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 10 || 40 < AD;
                break;
              case column_name.hb:
                HB < 8 || 42 < HB;
                break;
              case column_name.tp:
                error = TP < 14 || 52 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 52 < HD;
                break;
              case column_name.im:
                error = IM < 15 || 45 < IM;
                break;
              case column_name.hf:
                error = HF < 12 || 45 < HF;
                break;
              default:
                error = false;
            }
            break;
          default:
            break;
        }
        break;
      case 10: // 7I
        switch (line[0]) {
          case "前傾ライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 6 || AD + 5 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 6 || AD + 5 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 6 || AD + 7 < HD;
                break;
              case column_name.im:
                error = IM < AD - 7 || AD + 5 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 13 || AD + 4 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターポジション":
            switch (columns) {
              case column_name.ad:
                error = AD < -7 || 1 < AD;
                break;
              case column_name.hb:
                error = HB < -7 || 1 < HB;
                break;
              case column_name.tp:
                error = TP < -8 || 1 < TP;
                break;
              case column_name.hd:
                error = HD < -4 || 5 < HD;
                break;
              case column_name.im:
                error = IM < -1 || 6 < IM;
                break;
              case column_name.hf:
                error = HF < 0 || 13 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターライン":
            switch (columns) {
              case column_name.ad:
                error = AD < -7 || 3 < AD;
                break;
              case column_name.hb:
                error = HB < -8 || 2 < HB;
                break;
              case column_name.tp:
                error = TP < -8 || 3 < TP;
                break;
              case column_name.hd:
                error = HD < -8 || 2 < HD;
                break;
              case column_name.im:
                error = IM < -12 || -1 < IM;
                break;
              case column_name.hf:
                error = HF < -14 || 1 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ライトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 5 || AD + 2 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 5 || AD + 3 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 5 || AD + 8 < HD;
                break;
              case column_name.im:
                error = IM < AD + 2 || AD + 9 < IM;
                break;
              case column_name.hf:
                error = HF < AD + 2 || AD + 20 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "レフトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD || AD + 12 < HB;
                break;
              case column_name.tp:
                error = TP < AD || AD + 12 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 4 || AD + 3 < HD;
                break;
              case column_name.im:
                error = IM < AD - 8 || AD + 2 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 8 || AD + 5 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ネックポジション":
            switch (columns) {
              case column_name.hb:
                error = HB < -10 || 0 < HB;
                break;
              case column_name.tp:
                error = TP < -9 || 0 < TP;
                break;
              case column_name.hd:
                error = HD < -6 || 3 < HD;
                break;
              case column_name.im:
                error = IM < -4 || 3 < IM;
                break;
              case column_name.hf:
                error = HF < -3 || 4 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "右膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 15 || 40 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 40 < HB;
                break;
              case column_name.tp:
                error = TP < 2 || 40 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 65 < HD;
                break;
              case column_name.im:
                error = IM < 25 || 55 < IM;
                break;
              case column_name.hf:
                error = HF < 20 || 50 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "左膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 10 || 40 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 42 < HB;
                break;
              case column_name.tp:
                error = TP < 14 || 52 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 52 < HD;
                break;
              case column_name.im:
                error = IM < 15 || 45 < IM;
                break;
              case column_name.hf:
                error = HF < 12 || 45 < HF;
                break;
              default:
                error = false;
            }
            break;
          default:
            break;
        }
        break;
      case 13: // PW
        switch (line[0]) {
          // todo: score
          /*
					case "前傾ライン":
						switch (columns) {
							case column_name.hb:
								error = ((HB < AD - 4) || (AD + 3 < HB));
								break;
							case column_name.tp:
								error = ((TP < AD - 4) || (AD + 3 < TP));
								break;
							case column_name.hd:
								error = ((HD < AD - 4) || (AD + 4 < HD));
								break;
							case column_name.im:
								error = ((IM < AD - 4) || (AD + 4 < IM));
								break;
							case column_name.hf:
								error = ((HF < AD - 13) || (AD + 4 < HF));
								break;
							default:
								error = false;
						}
						break;
					*/
          case "前傾ライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 6 || AD + 5 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 6 || AD + 5 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 6 || AD + 7 < HD;
                break;
              case column_name.im:
                error = IM < AD - 7 || AD + 5 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 13 || AD + 4 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターポジション":
            switch (columns) {
              case column_name.ad:
                error = AD < -5 || 2 < AD;
                break;
              case column_name.hb:
                error = HB < -5 || 2 < HB;
                break;
              case column_name.tp:
                error = TP < -6 || 2 < TP;
                break;
              case column_name.hd:
                error = HD < -4 || 4 < HD;
                break;
              case column_name.im:
                error = IM < -1 || 7 < IM;
                break;
              case column_name.hf:
                error = HF < 0 || 10 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "センターライン":
            switch (columns) {
              case column_name.ad:
                error = AD < -6 || 3 < AD;
                break;
              case column_name.hb:
                error = HB < -6 || 3 < HB;
                break;
              case column_name.tp:
                error = TP < -6 || 3 < TP;
                break;
              case column_name.hd:
                error = HD < -6 || 3 < HD;
                break;
              case column_name.im:
                error = IM < -8 || -3 < IM;
                break;
              case column_name.hf:
                error = HF < -10 || 3 < HF;
                break;
              default:
                error = false;
            }
            break;
          // todo: score
          /*
					case "ライトライン":
						switch (columns) {
							case column_name.hb:
								error = ((HB < AD - 4) || (AD + 3 < HB));
								break;
							case column_name.tp:
								error = ((TP < AD - 4) || (AD + 4 < TP));
								break;
							case column_name.hd:
								error = ((HD < AD - 5) || (AD + 8 < HD));
								break;
							case column_name.im:
								error = ((IM < AD + 2) || (AD + 9 < IM));
								break;
							case column_name.hf:
								error = ((HF < AD + 2) || (AD + 20 < HF));
								break;
							default:
								error = false;
						}
						break;
					case "レフトライン":
						switch (columns) {
							case column_name.hb:
								error = ((HB < AD) || (AD + 12 < HB));
								break;
							case column_name.tp:
								error = ((TP < AD) || (AD + 12 < TP));
								break;
							case column_name.hd:
								error = ((HD < AD - 5) || (AD + 2 < HD));
								break;
							case column_name.im:
								error = ((IM < AD - 6) || (AD + 1 < IM));
								break;
							case column_name.hf:
								error = ((HF < AD - 6) || (AD + 5 < HF));
								break;
							default:
								error = false;
						}
						break;
					*/
          case "ライトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD - 5 || AD + 2 < HB;
                break;
              case column_name.tp:
                error = TP < AD - 5 || AD + 3 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 5 || AD + 8 < HD;
                break;
              case column_name.im:
                error = IM < AD + 2 || AD + 9 < IM;
                break;
              case column_name.hf:
                error = HF < AD + 2 || AD + 20 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "レフトライン":
            switch (columns) {
              case column_name.hb:
                error = HB < AD || AD + 12 < HB;
                break;
              case column_name.tp:
                error = TP < AD || AD + 12 < TP;
                break;
              case column_name.hd:
                error = HD < AD - 4 || AD + 3 < HD;
                break;
              case column_name.im:
                error = IM < AD - 8 || AD + 2 < IM;
                break;
              case column_name.hf:
                error = HF < AD - 8 || AD + 5 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "ネックポジション":
            switch (columns) {
              case column_name.hb:
                error = HB < -10 || 0 < HB;
                break;
              case column_name.tp:
                error = TP < -9 || 0 < TP;
                break;
              case column_name.hd:
                error = HD < -6 || 3 < HD;
                break;
              case column_name.im:
                error = IM < -4 || 3 < IM;
                break;
              case column_name.hf:
                error = HF < -3 || 4 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "右膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 15 || 40 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 40 < HB;
                break;
              case column_name.tp:
                error = TP < 2 || 40 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 65 < HD;
                break;
              case column_name.im:
                error = IM < 25 || 55 < IM;
                break;
              case column_name.hf:
                error = HF < 20 || 50 < HF;
                break;
              default:
                error = false;
            }
            break;
          case "左膝の角度":
            switch (columns) {
              case column_name.ad:
                error = AD < 10 || 40 < AD;
                break;
              case column_name.hb:
                error = HB < 8 || 42 < HB;
                break;
              case column_name.tp:
                error = TP < 14 || 52 < TP;
                break;
              case column_name.hd:
                error = HD < 25 || 52 < HD;
                break;
              case column_name.im:
                error = IM < 15 || 45 < IM;
                break;
              case column_name.hf:
                error = HF < 12 || 45 < HF;
                break;
              default:
                error = false;
            }
            break;
          default:
            break;
        }
        break;
      default:
    }
    return error;
  }

  //  			前傾角度					重心の動き_正面				背骨の傾き					右腰の動き						左腰の動き						首元の動き				右膝の角度									左膝の角度
  // 				前傾ライン(°)				センターポジション(cm)		センターライン(cm)			ライトライン(°)					レフトライン(°)					ネックポジション(cm)		右膝の角度(°)								左膝の角度(°)
  // 1W
  public phase_table_1W(AD: number): any {
    return {
      AD: [
        { min: AD, max: AD },
        { min: -7, max: 0 },
        { min: -6, max: 3 },
        { min: AD, max: AD },
        { min: AD, max: AD },
        { min: 0, max: 0 },
        { min: 16, max: 38 },
        { min: 0, max: 0 },
        { min: 6, max: 33 },
      ],
      HB: [
        { min: AD - 8, max: AD + 5 },
        { min: -7, max: 0 },
        { min: -6, max: 3 },
        { min: AD - 5, max: AD + 2 },
        { min: AD, max: AD + 12 },
        { min: -10, max: 0 },
        { min: 8, max: 40 },
        { min: 0, max: 0 },
        { min: 8, max: 38 },
      ],
      TP: [
        { min: AD - 8, max: AD + 5 },
        { min: -9, max: 0 },
        { min: -8, max: 3 },
        { min: AD - 5, max: AD + 3 },
        { min: AD, max: AD + 12 },
        { min: -11, max: -1 },
        { min: 2, max: 40 },
        { min: 0, max: 0 },
        { min: 14, max: 55 },
      ],
      HD: [
        { min: AD - 10, max: AD + 7 },
        { min: -5, max: 4 },
        { min: -12, max: 0 },
        { min: AD - 4, max: AD + 8 },
        { min: AD - 4, max: AD + 5 },
        { min: -10, max: 1 },
        { min: 25, max: 65 },
        { min: 0, max: 0 },
        { min: 25, max: 58 },
      ],
      IM: [
        { min: AD - 10, max: AD + 3 },
        { min: -2, max: 5 },
        { min: -16, max: -3 },
        { min: AD + 2, max: AD + 10 },
        { min: AD - 8, max: AD + 2 },
        { min: -12, max: 2 },
        { min: 25, max: 55 },
        { min: 0, max: 0 },
        { min: 16, max: 45 },
      ],
      HF: [
        { min: AD - 18, max: AD + 2 },
        { min: -3, max: 5 },
        { min: -16, max: 1 },
        { min: AD + 2, max: AD + 20 },
        { min: AD - 8, max: AD + 5 },
        { min: -9, max: 2 },
        { min: 20, max: 50 },
        { min: 0, max: 0 },
        { min: 7, max: 35 },
      ],
      FN: [
        { min: -2, max: 0 },
        { min: -2, max: 5 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: 0, max: 0 },
        { min: -2, max: 0 },
      ],
    };
  }
  //  			前傾角度					重心の動き_正面				背骨の傾き					右腰の動き						左腰の動き						首元の動き				右膝の角度									左膝の角度
  // 				前傾ライン(°)				センターポジション(cm)		センターライン(cm)			ライトライン(°)					レフトライン(°)					ネックポジション(cm)		右膝の角度(°)								左膝の角度(°)
  // 5W
  public phase_table_5W(AD: number): any {
    return {
      AD: [
        { min: AD, max: AD },
        { min: -7, max: 1 },
        { min: -7, max: 3 },
        { min: AD, max: AD },
        { min: AD, max: AD },
        { min: 0, max: 0 },
        { min: 15, max: 40 },
        { min: 0, max: 0 },
        { min: 10, max: 40 },
      ],
      HB: [
        { min: AD - 7, max: AD + 5 },
        { min: -7, max: 1 },
        { min: -8, max: 2 },
        { min: AD - 5, max: AD + 2 },
        { min: AD, max: AD + 12 },
        { min: -10, max: 0 },
        { min: 8, max: 40 },
        { min: 0, max: 0 },
        { min: 8, max: 42 },
      ],
      TP: [
        { min: AD - 7, max: AD + 5 },
        { min: -8, max: 1 },
        { min: -8, max: 3 },
        { min: AD - 5, max: AD + 3 },
        { min: AD, max: AD + 12 },
        { min: -9, max: 0 },
        { min: 2, max: 40 },
        { min: 0, max: 0 },
        { min: 14, max: 52 },
      ],
      HD: [
        { min: AD - 8, max: AD + 7 },
        { min: -5, max: 3 },
        { min: -8, max: 2 },
        { min: AD - 5, max: AD + 8 },
        { min: AD - 4, max: AD + 3 },
        { min: -6, max: 3 },
        { min: 25, max: 65 },
        { min: 0, max: 0 },
        { min: 25, max: 52 },
      ],
      IM: [
        { min: AD - 8, max: AD + 5 },
        { min: -3, max: 5 },
        { min: -12, max: -1 },
        { min: AD + 2, max: AD + 9 },
        { min: AD - 8, max: AD + 2 },
        { min: -4, max: 3 },
        { min: 25, max: 55 },
        { min: 0, max: 0 },
        { min: 15, max: 45 },
      ],
      HF: [
        { min: AD - 12, max: AD + 3 },
        { min: -2, max: 8 },
        { min: -14, max: 1 },
        { min: AD + 2, max: AD + 20 },
        { min: AD - 8, max: AD + 5 },
        { min: -3, max: 4 },
        { min: 20, max: 50 },
        { min: 0, max: 0 },
        { min: 12, max: 45 },
      ],
      FN: [
        { min: -2, max: 0 },
        { min: -2, max: 5 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: 0, max: 0 },
        { min: -2, max: 0 },
      ],
    };
  }

  //  			前傾角度					重心の動き_正面				背骨の傾き					右腰の動き						左腰の動き						首元の動き				右膝の角度									左膝の角度
  // 				前傾ライン(°)				センターポジション(cm)		センターライン(cm)			ライトライン(°)					レフトライン(°)					ネックポジション(cm)		右膝の角度(°)								左膝の角度(°)
  // 7I
  public phase_table_7I(AD: number): any {
    return {
      AD: [
        { min: AD, max: AD },
        { min: -7, max: 1 },
        { min: -7, max: 3 },
        { min: AD, max: AD },
        { min: AD, max: AD },
        { min: 0, max: 0 },
        { min: 15, max: 40 },
        { min: 0, max: 0 },
        { min: 10, max: 40 },
      ],
      HB: [
        { min: AD - 6, max: AD + 5 },
        { min: -7, max: 1 },
        { min: -8, max: 2 },
        { min: AD - 5, max: AD + 2 },
        { min: AD, max: AD + 12 },
        { min: -10, max: 0 },
        { min: 8, max: 40 },
        { min: 0, max: 0 },
        { min: 8, max: 42 },
      ],
      TP: [
        { min: AD - 6, max: AD + 5 },
        { min: -8, max: 1 },
        { min: -8, max: 3 },
        { min: AD - 5, max: AD + 3 },
        { min: AD, max: AD + 12 },
        { min: -9, max: 0 },
        { min: 2, max: 40 },
        { min: 0, max: 0 },
        { min: 14, max: 52 },
      ],
      HD: [
        { min: AD - 6, max: AD + 7 },
        { min: -4, max: 5 },
        { min: -8, max: 2 },
        { min: AD - 5, max: AD + 8 },
        { min: AD - 4, max: AD + 3 },
        { min: -6, max: 3 },
        { min: 25, max: 65 },
        { min: 0, max: 0 },
        { min: 25, max: 52 },
      ],
      IM: [
        { min: AD - 7, max: AD + 5 },
        { min: -1, max: 6 },
        { min: -12, max: -1 },
        { min: AD + 2, max: AD + 9 },
        { min: AD - 8, max: AD + 2 },
        { min: -4, max: 3 },
        { min: 25, max: 55 },
        { min: 0, max: 0 },
        { min: 15, max: 45 },
      ],
      HF: [
        { min: AD - 13, max: AD + 3 },
        { min: 0, max: 13 },
        { min: -14, max: 1 },
        { min: AD + 2, max: AD + 20 },
        { min: AD - 8, max: AD + 5 },
        { min: -3, max: 4 },
        { min: 20, max: 50 },
        { min: 0, max: 0 },
        { min: 12, max: 45 },
      ],
      FN: [
        { min: -2, max: 0 },
        { min: -2, max: 5 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: 0, max: 0 },
        { min: -2, max: 0 },
      ],
    };
  }

  //  			前傾角度					重心の動き_正面				背骨の傾き					右腰の動き						左腰の動き						首元の動き				右膝の角度									左膝の角度
  // 				前傾ライン(°)				センターポジション(cm)		センターライン(cm)			ライトライン(°)					レフトライン(°)					ネックポジション(cm)		右膝の角度(°)								左膝の角度(°)
  // PW
  public phase_table_PW(AD: number): any {
    // todo: score
    /*
		return {
			AD: [{min: AD, max: AD}, 			{min: -5, max: 2}, 		{min: -6, max: 3}, 		{min: AD, max: AD}, 			{min: AD, max: AD}, 		{min: 0, max: 0}, 		{min: 15, max: 40}, 	{min: 0, max: 0}, 	{min: 10, max: 40}],
			HB: [{min: AD - 4, max: AD + 3}, 	{min: -5, max: 2}, 		{min: -6, max: 3}, 		{min: AD - 4, max: AD + 3}, 	{min: AD, max: AD + 12}, 	{min: -6, max: 2}, 		{min: 8, max: 40}, 		{min: 0, max: 0}, 	{min: 8, max: 42}],
			TP: [{min: AD - 4, max: AD + 3}, 	{min: -6, max: 2}, 		{min: -6, max: 3}, 		{min: AD - 4, max: AD + 4}, 	{min: AD, max: AD + 12}, 	{min: -6, max: 2}, 		{min: 2, max: 40}, 		{min: 0, max: 0}, 	{min: 14, max: 52}],
			HD: [{min: AD - 4, max: AD + 4}, 	{min: -4, max: 4}, 		{min: -6, max: 3}, 		{min: AD - 5, max: AD + 8}, 	{min: AD - 5, max: AD + 2}, {min: -4, max: 4}, 		{min: 25, max: 65}, 	{min: 0, max: 0}, 	{min: 25, max: 52}],
			IM: [{min: AD - 4, max: AD + 4}, 	{min: -1, max: 5}, 		{min: -8, max: 3}, 		{min: AD + 2, max: AD + 9}, 	{min: AD - 6, max: AD + 1}, {min: -3, max: 5}, 		{min: 25, max: 55}, 	{min: 0, max: 0}, 	{min: 15, max: 45}],
			HF: [{min: AD - 6, max: AD + 3}, 	{min: 0, max: 10}, 		{min: -10, max: 3}, 	{min: AD + 2, max: AD + 20}, 	{min: AD - 6, max: AD + 5}, {min: -2, max: 6}, 		{min: 20, max: 50}, 	{min: 0, max: 0}, 	{min: 12, max: 45}],
			FN: [{min: -2, max: 0}, 			{min: -1, max: 5}, 		{min: -2, max: 0}, 		{min: -2, max: 0}, 				{min: -2, max: 0}, 			{min: -2, max: 0}, 		{min: -2, max: 0}, 		{min: 0, max: 0}, 	{min: -2, max: 0}],
		};
		 */
    return {
      AD: [
        { min: AD, max: AD },
        { min: -5, max: 2 },
        { min: -6, max: 3 },
        { min: AD, max: AD },
        { min: AD, max: AD },
        { min: 0, max: 0 },
        { min: 15, max: 40 },
        { min: 0, max: 0 },
        { min: 10, max: 40 },
      ],
      HB: [
        { min: AD - 6, max: AD + 5 },
        { min: -5, max: 2 },
        { min: -6, max: 3 },
        { min: AD - 5, max: AD + 2 },
        { min: AD, max: AD + 12 },
        { min: -6, max: 2 },
        { min: 8, max: 40 },
        { min: 0, max: 0 },
        { min: 8, max: 42 },
      ],
      TP: [
        { min: AD - 6, max: AD + 5 },
        { min: -6, max: 2 },
        { min: -6, max: 3 },
        { min: AD - 5, max: AD + 3 },
        { min: AD, max: AD + 12 },
        { min: -6, max: 2 },
        { min: 2, max: 40 },
        { min: 0, max: 0 },
        { min: 14, max: 52 },
      ],
      HD: [
        { min: AD - 6, max: AD + 7 },
        { min: -4, max: 4 },
        { min: -6, max: 3 },
        { min: AD - 5, max: AD + 8 },
        { min: AD - 4, max: AD + 3 },
        { min: -4, max: 4 },
        { min: 25, max: 65 },
        { min: 0, max: 0 },
        { min: 25, max: 52 },
      ],
      IM: [
        { min: AD - 7, max: AD + 5 },
        { min: -1, max: 5 },
        { min: -8, max: 3 },
        { min: AD + 2, max: AD + 9 },
        { min: AD - 8, max: AD + 2 },
        { min: -3, max: 5 },
        { min: 25, max: 55 },
        { min: 0, max: 0 },
        { min: 15, max: 45 },
      ],
      HF: [
        { min: AD - 13, max: AD + 3 },
        { min: 0, max: 10 },
        { min: -10, max: 3 },
        { min: AD + 2, max: AD + 20 },
        { min: AD - 8, max: AD + 5 },
        { min: -2, max: 6 },
        { min: 20, max: 50 },
        { min: 0, max: 0 },
        { min: 12, max: 45 },
      ],
      FN: [
        { min: -2, max: 0 },
        { min: -1, max: 5 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: -2, max: 0 },
        { min: 0, max: 0 },
        { min: -2, max: 0 },
      ],
    };
  }
}
